import type { MessageKey } from './fr';

/**
 * English message catalogue. Secondary language (PDF §27): included for
 * completeness of the locale switcher, not because English copy has been
 * requested for the MVP.
 */
export const en: Record<MessageKey, string> = {
  'common.loading': 'Loading…',
  'common.close': 'Close',
  'common.dismiss': 'Dismiss',
  'common.retry': 'Try again',
  'common.required': 'Required',
  'common.optional': 'Optional',
  'common.tempPassword.title': 'Provisional password',
  'common.tempPassword.once':
    'This password is shown only once. Write it down now and pass it on through a safe channel.',
  'common.tempPassword.account': 'Account',
  'common.tempPassword.label': 'Provisional password',
  'common.tempPassword.show': 'Show',
  'common.tempPassword.hide': 'Hide',
  'common.tempPassword.copy': 'Copy',
  'common.tempPassword.copied': 'Password copied',
  'common.tempPassword.copyFailed': 'Could not copy: write the password down by hand.',
  'common.tempPassword.expires': 'Valid until {{date}}. After that a new one must be generated.',
  'common.tempPassword.mustChange':
    'The person will have to choose their own password at their first sign-in.',
  'common.tempPassword.clipboard':
    'Your clipboard may keep a copy of this password: clear it after use.',
  'common.tempPassword.done': 'I have noted the password',
  'common.tempPassword.gone':
    'The password is no longer displayed. If it was not noted, generate a new one.',
  'common.changePassword.current': 'Current password',
  'common.changePassword.new': 'New password',
  'common.changePassword.confirm': 'Confirm the new password',
  'common.changePassword.rules': 'At least 12 characters, different from the current password.',
  'common.changePassword.submit': 'Save my password',
  'common.changePassword.currentRequired': 'Enter your current password.',
  'common.changePassword.tooShort': 'The new password must be at least 12 characters long.',
  'common.changePassword.mismatch': 'The two passwords do not match.',
  'common.changePassword.same': 'Choose a password different from the current one.',
  'common.selectPlaceholder': 'Select…',

  'validation.required': 'This field is required.',
  'validation.phoneInvalid': 'Invalid Tunisian phone number.',
  'validation.governorateRequired': 'Please select a governorate.',
  'validation.delegationRequired': 'Please select a delegation.',

  'phone.countryCode': '+216',
  'phone.placeholder': '22 345 678',

  'governorate.label': 'Governorate',
  'delegation.label': 'Delegation',

  'pagination.label': 'Pagination',
  'pagination.previous': 'Previous page',
  'pagination.next': 'Next page',
  'pagination.page': 'Page',

  'stepper.label': 'Progress',
  'stepper.completed': 'Completed step',
  'stepper.current': 'Current step',
  'stepper.upcoming': 'Upcoming step',

  // ---- storefront: navigation ----
  'storefront.nav.shop': 'Shop',
  'storefront.nav.categories': 'Categories',
  'storefront.nav.products': 'Products',
  'storefront.nav.orderTracking': 'Track an order',
  'storefront.nav.cart': 'Cart',
  'storefront.nav.cartWithCount': 'Cart — {{count}} item{{plural}}',
  'storefront.nav.primary': 'Main navigation',
  'storefront.nav.menuOpen': 'Open the menu',
  'storefront.nav.menuClose': 'Close the menu',
  'storefront.nav.breadcrumb': 'Breadcrumb',

  // ---- storefront: footer ----
  'storefront.footer.contact': 'Contact',
  'storefront.footer.hours': 'Opening hours',
  'storefront.footer.policies': 'Policies',
  'storefront.footer.shipping': 'Shipping',
  'storefront.footer.returns': 'Returns',
  'storefront.footer.terms': 'Terms of service',
  'storefront.footer.privacy': 'Privacy',
  'storefront.footer.usefulLinks': 'Useful links',
  'storefront.footer.buyWithConfidence': 'Shop with confidence',
  'storefront.footer.cod': 'Cash on delivery',
  'storefront.footer.deliveryNational': 'Delivery to all 24 governorates',
  'storefront.footer.deliveryFee': 'Delivery fee: {{fee}}',
  'storefront.footer.noDeliveryFee': 'No delivery fee',
  'storefront.footer.whatsappCta': 'Chat on WhatsApp',
  'storefront.footer.call': 'Call us',
  'storefront.footer.poweredBy': 'Powered by ABOUEB',
  'storefront.footer.rights': '© {{year}} {{name}}',
  'storefront.footer.day.monday': 'Monday',
  'storefront.footer.day.tuesday': 'Tuesday',
  'storefront.footer.day.wednesday': 'Wednesday',
  'storefront.footer.day.thursday': 'Thursday',
  'storefront.footer.day.friday': 'Friday',
  'storefront.footer.day.saturday': 'Saturday',
  'storefront.footer.day.sunday': 'Sunday',

  // ---- storefront: home ----
  'storefront.home.ctaProducts': 'Discover products',
  'storefront.home.ctaCategories': 'Browse categories',
  'storefront.collections.title': 'Our collections',
  'storefront.collections.count': '{{count}} product{{plural}}',
  'storefront.collections.all': 'All collections',
  'storefront.story.eyebrow': 'About',
  'storefront.story.defaultTitle': 'Our story',
  'storefront.story.cta': 'Discover our products',
  'storefront.featured.eyebrow': "The shop's selection",
  'storefront.featured.title': 'Our favourites',
  'storefront.featured.subtitle': 'The products {{shopName}} recommends to you.',
  'storefront.featured.badge': 'Favourite',
  'storefront.featured.cta': 'Discover',
  'storefront.newArrivals.eyebrow': 'Just landed',
  'storefront.newArrivals.title': 'New arrivals',
  'storefront.newArrivals.badge': 'New',
  'storefront.newArrivals.prev': 'Previous',
  'storefront.newArrivals.next': 'Next',
  'storefront.home.lowStockTitle': 'Limited stock',
  'storefront.home.lowStockBadge': 'Last items',
  'storefront.home.productsTitle': 'Our products',
  'storefront.home.viewAll': 'View all',
  'storefront.home.emptyProducts': 'This shop has not published any products yet.',

  // ---- storefront: reassurance bar ----
  'storefront.reassurance.codTitle': 'Cash on delivery',
  'storefront.reassurance.codDescription':
    'Pay in cash when your parcel arrives — no online payment.',
  'storefront.reassurance.deliveryTitle': 'Nationwide delivery',
  'storefront.reassurance.returnsTitle': 'Returns',
  'storefront.reassurance.returnsDescription':
    'The shop has published its return policy: read it before you order.',
  'storefront.trust.deliveryWithFee': 'to all 24 governorates. Delivery fee: {{fee}}.',
  'storefront.trust.deliveryNoFee': 'to all 24 governorates, no delivery fee.',
  'storefront.trust.deliveryStripWithFee': 'To all 24 governorates · fee: {{fee}}',
  'storefront.trust.deliveryStripNoFee': 'To all 24 governorates · no fee',
  'storefront.trust.returnsSummary': "The shop's return policy",
  'storefront.trust.contactTitle': 'A question? Write to or call the shop.',
  'storefront.trust.email': 'Send an email',
  'storefront.common.noFee': 'No fee',
  'storefront.reassurance.supportTitle': 'Customer support',

  // ---- storefront: categories page ----
  'storefront.categories.title': 'Categories',
  'storefront.categories.empty': 'This shop has not published any categories yet.',
  'storefront.categories.count': '{{count}} collection{{plural}}',
  'storefront.categories.discover': 'Discover',
  'storefront.categories.moreLabel': '{{count}} more subcategories in {{name}}',

  // ---- storefront: products (catalogue) page ----
  'storefront.products.title': 'Products',
  'storefront.products.viewAll': 'View all products',
  'storefront.products.emptyCategory': 'No products published in this category yet.',
  'storefront.products.emptyAll': 'This shop has not published any products yet.',
  'storefront.catalog.allProducts': 'All products',
  'storefront.catalog.chipAll': 'All',
  'storefront.catalog.chipsLabel': 'Categories',
  'storefront.catalog.count': '{{count}} product{{plural}}',
  'storefront.catalog.filter': 'Filter',
  'storefront.catalog.filterWithCount': 'Filter · {{count}}',
  'storefront.catalog.sortLabel': 'Sort by',
  'storefront.catalog.sort.featured': 'Featured',
  'storefront.catalog.sort.newest': 'Newest',
  'storefront.catalog.sort.priceAsc': 'Price: low to high',
  'storefront.catalog.sort.priceDesc': 'Price: high to low',
  'storefront.catalog.sort.nameAsc': 'Name A–Z',
  'storefront.catalog.filters.title': 'Filters',
  'storefront.catalog.filters.close': 'Close',
  'storefront.catalog.filters.price': 'Price',
  'storefront.catalog.filters.priceMin': 'Min',
  'storefront.catalog.filters.priceMax': 'Max',
  'storefront.catalog.filters.currency': 'TND',
  'storefront.catalog.filters.availability': 'Availability',
  'storefront.catalog.filters.inStock': 'In stock only',
  'storefront.catalog.filters.promotions': 'Promotions',
  'storefront.catalog.filters.onSale': 'On sale',
  'storefront.catalog.filters.brand': 'Brand',
  'storefront.catalog.filters.clear': 'Clear',
  'storefront.catalog.filters.apply': 'Show {{count}} result{{plural}}',
  'storefront.catalog.active.groupLabel': 'Active filters',
  'storefront.catalog.active.priceRange': '{{min}} – {{max}} TND',
  'storefront.catalog.active.priceFrom': 'From {{min}} TND',
  'storefront.catalog.active.priceUpTo': 'Up to {{max}} TND',
  'storefront.catalog.active.inStock': 'In stock',
  'storefront.catalog.active.onSale': 'On sale',
  'storefront.catalog.active.clearAll': 'Clear all',
  'storefront.catalog.active.remove': 'Remove filter: {{label}}',
  'storefront.catalog.showMore': 'Show {{count}} more',
  'storefront.catalog.progress': 'You have seen {{shown}} of {{total}} products',
  'storefront.catalog.emptyFiltered': 'No product matches these filters.',
  'storefront.catalog.clearFilters': 'Clear filters',
  'storefront.card.add': 'Add',
  'storefront.card.chooseOptions': 'Choose options',
  'storefront.card.unavailable': 'Unavailable',
  'storefront.card.soldOut': 'Sold out',
  'storefront.card.lowStockLine': 'Only {{count}} left',
  'storefront.wishlist.add': 'Add to favourites',
  'storefront.wishlist.remove': 'Remove from favourites',
  'storefront.wishlist.navLabel': 'My favourites',
  'storefront.wishlist.navLabelWithCount': 'My favourites — {{count}}',
  'storefront.wishlist.title': 'My favourites',
  'storefront.wishlist.deviceNote': 'Favourites saved on this device',
  'storefront.wishlist.deviceNoteSub': 'They are not synced with an account.',
  'storefront.wishlist.empty': "You don't have any favourites yet.",
  'storefront.wishlist.emptyHint': "Tap a product's heart to find it here.",
  'storefront.wishlist.emptyCta': 'Browse the catalogue',
  'storefront.wishlist.loadError': 'Your favourites could not be loaded right now.',

  // ---- storefront: product detail ----
  'storefront.product.similar': 'You may also like',
  'storefront.product.viewPhoto': 'View photo {{n}}',
  'storefront.product.addToCart': 'Add to cart',
  'storefront.product.addShort': 'Add',
  'storefront.product.adding': 'Adding…',
  'storefront.product.addedToCart': 'Added to cart.',
  'storefront.product.quickAddLabel': 'Quick add to cart',
  'storefront.product.quickAddedLabel': 'Added to cart',
  'storefront.product.notFoundTitle': 'Product not found',
  'storefront.product.notFoundDescription':
    'This product does not exist or is no longer available.',
  'storefront.product.selectOptionsPrompt': 'Choose an option',
  'storefront.product.combinationUnavailable': 'This combination is not available.',

  // ---- storefront: stock states ----
  'storefront.stock.inStock': 'In stock',
  'storefront.stock.inStockCount': 'In stock · {{count}}',
  'storefront.stock.lowStock': 'Limited stock',
  'storefront.stock.outOfStock': 'Out of stock',
  'storefront.stock.unavailable': 'Unavailable',
  'storefront.stock.badgeOutOfStock': 'Sold out',
  'storefront.stock.badgeLowStock': 'Only {{count}} left',

  // ---- storefront: shared across cart / checkout / order lookup ----
  'storefront.common.subtotal': 'Subtotal',
  'storefront.common.discount': 'Discount',
  'storefront.common.deliveryFee': 'Delivery',
  'storefront.common.total': 'Total',
  'storefront.common.codReassurance': 'Cash on delivery — you only pay on receipt.',
  'storefront.common.backToShop': 'Back to shop',
  'storefront.common.emptyCart': 'Your cart is empty.',

  // ---- storefront: cart page ----
  'storefront.cart.title': 'Cart',
  'storefront.cart.remove': 'Remove',
  'storefront.cart.removedMessage': '{{name}} removed from cart.',
  'storefront.cart.couponPlaceholder': 'Promo code',
  'storefront.cart.couponLabel': 'Promo code',
  'storefront.cart.couponApply': 'Apply',
  'storefront.cart.couponApplied': 'Code "{{code}}" applied.',
  'storefront.cart.couponInvalid': 'Code "{{code}}" is no longer valid and was removed.',
  'storefront.cart.checkoutCta': 'Checkout',
  'storefront.cart.continueShopping': '← Continue shopping',

  // ---- storefront: checkout page ----
  'storefront.checkout.title': 'Checkout',
  'storefront.checkout.stepContact': 'Contact details',
  'storefront.checkout.stepDelivery': 'Delivery',
  'storefront.checkout.stepSummary': 'Summary',
  'storefront.checkout.submitPending': 'Submitting…',
  'storefront.checkout.submit': 'Confirm order',
  'storefront.checkout.sectionContact': 'Contact details',
  'storefront.checkout.fieldName': 'Full name',
  'storefront.checkout.fieldPhone': 'Phone',
  'storefront.checkout.fieldEmail': 'Email (optional)',
  'storefront.checkout.sectionDelivery': 'Delivery',
  'storefront.checkout.fieldGovernorate': 'Governorate',
  'storefront.checkout.fieldGovernoratePlaceholder': 'Choose…',
  'storefront.checkout.fieldDelegation': 'Delegation',
  'storefront.checkout.fieldDelegationFirst': 'Choose the governorate first',
  'storefront.checkout.errors.delegationMismatch':
    'This delegation is not in the chosen governorate.',
  'storefront.checkout.fieldAddress': 'Address',
  'storefront.checkout.fieldAddressExtra': 'Additional address info (optional)',
  'storefront.checkout.fieldNotes': 'Notes (optional)',
  'storefront.checkout.summaryTitle': 'Summary',
  'storefront.checkout.editCart': 'Edit cart',
  'storefront.checkout.codBadge': 'Popular',
  'storefront.checkout.codLabel': 'Cash on delivery',
  'storefront.checkout.noShippingFee': 'This shop charges no delivery fee.',
  'storefront.checkout.successTitle': 'Order confirmed!',
  'storefront.checkout.successPrefix': 'Your order',
  'storefront.checkout.successSuffix': 'has been successfully recorded.',
  'storefront.checkout.successTotal': 'Total due',
  'storefront.checkout.successCod': 'Cash on delivery — you only pay when your parcel arrives.',

  // ---- storefront: order lookup ----
  'storefront.checkout.errors.nameRequired': 'Your name is required.',
  'storefront.checkout.errors.phoneInvalid':
    'Invalid phone number: 8 digits, with or without +216, not starting with 0 or 1.',
  'storefront.checkout.errors.governorateRequired': 'The governorate is required.',
  'storefront.checkout.errors.delegationRequired': 'The delegation is required.',
  'storefront.checkout.errors.addressRequired': 'The address is required.',
  'storefront.orderLookup.errors.orderNumberInvalid': 'Invalid order number: digits only.',
  'storefront.orderLookup.title': 'Track an order',
  'storefront.orderLookup.subtitle':
    'Find your order using its number and the phone number used at checkout.',
  'storefront.orderLookup.orderNumberLabel': 'Order number',
  'storefront.orderLookup.phoneLabel': 'Phone number used for the order',
  'storefront.orderLookup.submit': 'Search',
  'storefront.orderLookup.submitPending': 'Searching…',
  'storefront.orderLookup.resultTitle': 'Order',
  'storefront.orderLookup.deliveryTitle': 'Delivery',
  'storefront.orderLookup.trackingTitle': 'Tracking',
  'storefront.orderLookup.parcel.title': 'Your parcel',
  'storefront.orderLookup.parcel.tracking': 'Tracking no.',
  'storefront.orderLookup.parcel.updated': 'Updated on',
  'storefront.orderLookup.parcel.status.registered': 'Parcel registered, waiting for pickup',
  'storefront.orderLookup.parcel.status.pickup_scheduled': 'Pickup scheduled',
  'storefront.orderLookup.parcel.status.in_transit': 'On its way',
  'storefront.orderLookup.parcel.status.out_for_delivery': 'Out for delivery',
  'storefront.orderLookup.parcel.status.delivered': 'Delivered',
  'storefront.orderLookup.parcel.status.return_in_progress': 'Delivery not possible — returning',
  'storefront.orderLookup.parcel.status.returned': 'Parcel returned to the shop',
  'storefront.orderLookup.parcel.status.checking':
    'We are checking your parcel — the shop will contact you',
  'storefront.orderLookup.parcel.status.cancelled': 'Shipment cancelled',
  'storefront.orderLookup.parcel.status.other': 'Being tracked',

  // ---- storefront: order status vocabulary ----
  'storefront.orderStatus.ORDER_CREATED': 'Created',
  'storefront.orderStatus.PAYMENT_PENDING': 'Payment pending',
  'storefront.orderStatus.PAYMENT_CONFIRMED': 'Cash on delivery',
  'storefront.orderStatus.PAYMENT_FAILED': 'Payment failed',
  'storefront.orderStatus.PROCESSING': 'Processing',
  'storefront.orderStatus.PACKED': 'Out for delivery',
  // The merchant sees the real step (handed to the carrier, not yet collected); the customer, a simpler « en livraison ».
  'merchant.orderStatus.PACKED': 'Packed',
  'storefront.orderStatus.CANCELLED': 'Cancelled',
  'storefront.orderStatus.SHIPPED': 'Out for delivery',
  'storefront.orderStatus.DELIVERY_FAILED': 'Delivery failed',
  'storefront.orderStatus.DELIVERED': 'Delivered',
  'storefront.orderStatus.RETURN_REQUESTED': 'Return requested',
  'storefront.orderStatus.RETURNED': 'Returned',
  'storefront.orderStatus.REFUNDED': 'Refunded',
  'storefront.orderStatus.PARTIALLY_REFUNDED': 'Partially refunded',

  // ---- storefront: not-found boundaries ----
  'storefront.notFound.shopTitle': 'Shop not found',
  'storefront.notFound.shopDescription': 'This shop does not exist or is not available right now.',

  // ---- storefront: platform landing ----
  'storefront.landing.title': 'ABOUEB',
  'storefront.landing.description':
    'Every ABOUEB shop has its own public address. Go directly to the link of the shop you are looking for to browse its catalogue.',

  // ---- merchant: shared layout (sidebar, topbar, command palette, shop switcher) ----
  'merchant.sidebar.brand': 'ABOUEB',
  'merchant.sidebar.subtitle': 'Merchant space',
  'merchant.sidebar.navLabel': 'Main navigation',
  'merchant.topbar.menuClose': 'Close the menu',
  'merchant.sidebar.collapse': 'Collapse the menu',
  'merchant.sidebar.expand': 'Expand the menu',

  'merchant.nav.group.principal': 'Main',
  'merchant.nav.group.operations': 'Operations',
  'merchant.nav.group.configuration': 'Configuration',
  'merchant.nav.section.general': 'General',
  'merchant.nav.section.sales': 'Sales',
  'merchant.nav.section.growth': 'Growth',
  'merchant.nav.section.manage': 'Manage',
  'merchant.nav.dashboard': 'Dashboard',
  'merchant.nav.orders': 'Orders',
  'merchant.nav.products': 'Products & stock',
  'merchant.nav.inventory': 'Inventory',
  'merchant.nav.customers': 'Customers',
  'merchant.nav.marketing': 'Marketing',
  'merchant.nav.analytics': 'Analytics',
  'merchant.nav.delivery': 'Delivery',
  'merchant.nav.deliveryAccount': 'Jibex account',
  'merchant.nav.payments': 'Payments & COD',
  'merchant.nav.invoices': 'Invoices',
  'merchant.nav.store': 'Shop',
  'merchant.nav.categories': 'Categories',
  'merchant.nav.promotions': 'Promotions',
  'merchant.nav.team': 'Team & permissions',
  'merchant.nav.settings': 'Settings',
  'merchant.nav.menu.products': 'Products',
  'merchant.nav.menu.payments': 'Payments',

  'merchant.topbar.searchPlaceholder': 'Search, go to…',
  'merchant.topbar.themeToggleAria': 'Change theme',
  'merchant.topbar.lightMode': 'Light mode',
  'merchant.topbar.darkMode': 'Dark mode',
  'merchant.topbar.logout': 'Log out',
  'merchant.topbar.menu': 'Open the menu',
  'merchant.topbar.more': 'More options',

  'merchant.shopSwitcher.none': 'No shop',
  'merchant.shopSwitcher.unnamed': 'Unnamed shop',
  'merchant.shopSwitcher.activeAria': 'Active shop',
  'merchant.shopSwitcher.suspended': 'suspended',

  'merchant.commandPalette.label': 'Command palette',
  'merchant.commandPalette.placeholder': 'Search a screen or an action…',
  'merchant.commandPalette.empty': 'No results.',
  'merchant.commandPalette.navigationGroup': 'Navigation',
  'merchant.commandPalette.preferencesGroup': 'Preferences',
  'merchant.commandPalette.toggleThemeToLight': 'Switch to light mode',
  'merchant.commandPalette.toggleThemeToDark': 'Switch to dark mode',

  'merchant.common.loadingAria': 'Loading',
  'merchant.common.noOrdersYet': 'No orders yet',
  'merchant.common.unexpectedError': 'An unexpected error occurred.',
  'merchant.common.cancel': 'Cancel',
  'merchant.common.confirm': 'Confirm',
  'merchant.common.save': 'Save',
  'merchant.common.edit': 'Edit',
  'merchant.common.delete': 'Delete',
  'merchant.common.accessDenied': 'Access denied',

  // ---- merchant: dashboard ----
  'merchant.dashboard.title': 'Dashboard',
  'merchant.dashboard.hello': 'Hello {{name}},',
  'merchant.dashboard.heroLead': 'here is your shop today.',
  'merchant.dashboard.addProduct': 'Add a product',
  'merchant.dashboard.rangeAria': 'Period',
  'merchant.dashboard.rangeDaysZero': '{{count}} days',
  'merchant.dashboard.rangeDaysOne': '{{count}} day',
  'merchant.dashboard.rangeDaysTwo': '{{count}} days',
  'merchant.dashboard.rangeDaysFew': '{{count}} days',
  'merchant.dashboard.rangeDaysMany': '{{count}} days',
  'merchant.dashboard.rangeDaysOther': '{{count}} days',
  'merchant.dashboard.kpiOrdersRange': 'Orders',
  'merchant.dashboard.kpiOrdersRangeHint': 'Over {{days}}',
  'merchant.dashboard.stockLow': 'Low stock',
  'merchant.dashboard.stockOut': 'Out of stock',
  'merchant.dashboard.salesEyebrow': 'Performance',
  'merchant.dashboard.salesLegend': 'Sales',
  'merchant.dashboard.salesTrend.subtitleRange': 'Over {{days}}, excluding cancellations',
  'merchant.dashboard.salesTrend.totalRange': 'Total over {{days}}',
  'merchant.dashboard.ordersEyebrow': 'Order flow',
  'merchant.dashboard.ordersTitle': 'Order status',
  'merchant.dashboard.viewAll': 'View all',
  'merchant.dashboard.ordersFilterAll': 'All',
  'merchant.dashboard.ordersFilterOpen': 'In progress',
  'merchant.dashboard.ordersFilterFinished': 'Completed',
  'merchant.dashboard.ordersTotal': 'orders in total',
  'merchant.dashboard.attentionEyebrow': 'Priorities',
  'merchant.dashboard.attentionTitle': 'Needs your attention',
  'merchant.dashboard.attentionCount': '{{count}} to handle',
  'merchant.dashboard.alerts.actionProcess': 'Process',
  'merchant.dashboard.alerts.actionCheck': 'Check',
  'merchant.dashboard.alerts.actionView': 'View',
  'merchant.dashboard.quickEyebrow': 'Shortcuts',
  'merchant.dashboard.quick.title': 'What would you like to do?',
  'merchant.dashboard.quick.newProductDescription': 'Add an item to your catalogue',
  'merchant.dashboard.quick.viewOrdersDescription': 'Handle the waiting orders',
  'merchant.dashboard.quick.inventory': 'Manage inventory',
  'merchant.dashboard.quick.inventoryDescription': 'Track stock levels',
  'merchant.dashboard.quick.newCouponDescription': 'Launch a promotional offer',
  'merchant.dashboard.quick.customers': 'Manage customers',
  'merchant.dashboard.quick.customersDescription': 'Browse your customers',
  'merchant.dashboard.quick.store': 'Set up the shop',
  'merchant.dashboard.quick.storeDescription': 'Customise your shop',
  'merchant.dashboard.shareEyebrow': 'Your shop',
  'merchant.dashboard.shareTitle': 'Share {{name}}',
  'merchant.dashboard.shareText': 'Introduce your shop to your community.',
  'merchant.dashboard.shareCopy': 'Copy',
  'merchant.dashboard.shareCopied': 'Copied',
  'merchant.dashboard.topEyebrow': 'Best sellers',
  'merchant.dashboard.subtitle': "What you've sold, your orders, and what needs your attention.",
  'merchant.dashboard.loadError': 'Unable to load the dashboard',
  'merchant.dashboard.accessDeniedDescription':
    'Your account does not have permission to view analytics.',
  'merchant.dashboard.kpiTodayRevenue': "Today's revenue",
  'merchant.dashboard.kpiTodayHint': '{{count}} order(s) today',
  'merchant.dashboard.kpiMonthRevenue': "This month's revenue",
  'merchant.dashboard.kpiMonthHint': '{{count}} order(s) this month',
  'merchant.dashboard.kpiOrderCount': 'Number of orders',
  'merchant.dashboard.kpiOrderCountHint': 'Since the shop opened',
  'merchant.dashboard.kpiActiveProducts': 'Active products',
  'merchant.dashboard.kpiTodayHintZero': '{{count}} orders today',
  'merchant.dashboard.kpiTodayHintOne': '{{count}} order today',
  'merchant.dashboard.kpiTodayHintTwo': '{{count}} orders today',
  'merchant.dashboard.kpiTodayHintFew': '{{count}} orders today',
  'merchant.dashboard.kpiTodayHintMany': '{{count}} orders today',
  'merchant.dashboard.kpiTodayHintOther': '{{count}} orders today',
  'merchant.dashboard.kpiMonthHintZero': '{{count}} orders this month',
  'merchant.dashboard.kpiMonthHintOne': '{{count}} order this month',
  'merchant.dashboard.kpiMonthHintTwo': '{{count}} orders this month',
  'merchant.dashboard.kpiMonthHintFew': '{{count}} orders this month',
  'merchant.dashboard.kpiMonthHintMany': '{{count}} orders this month',
  'merchant.dashboard.kpiMonthHintOther': '{{count}} orders this month',
  'merchant.dashboard.kpiMonthRevenueShort': 'Month revenue',
  'merchant.dashboard.kpiOrderCountShort': 'Orders',
  'merchant.dashboard.kpiActiveProductsShort': 'Products',
  'merchant.dashboard.trendStable': 'Stable vs yesterday',
  'merchant.dashboard.trendChange': '{{value}}% vs yesterday',
  'merchant.dashboard.trendNewActivity': 'New activity vs yesterday',
  'merchant.dashboard.statusByStatusTitle': 'Orders by status',
  'merchant.dashboard.statusByStatusSubtitle': 'All orders, since the shop opened',
  'merchant.dashboard.statusBreakdown.emptyDescription':
    'The status breakdown will appear here as soon as there is a first order.',
  'merchant.dashboard.kpiSoonBadge': 'Coming soon',
  'merchant.dashboard.kpiSoonDefaultReason': 'Module not built yet.',

  'merchant.dashboard.alerts.title': 'Alerts',
  'merchant.dye.title': 'Your orders, stage by stage',
  'merchant.dye.subtitle': 'Every order since the shop opened, from received to delivered',
  'merchant.dye.stage.received': 'Received',
  'merchant.dye.stage.toPrepare': 'To prepare',
  'merchant.dye.stage.ready': 'Ready',
  'merchant.dye.stage.shipping': 'Out for delivery',
  'merchant.dye.stage.delivered': 'Delivered',
  'merchant.dye.outOfFlow': 'Left the flow',
  'merchant.dye.processZero': 'Process {{count}} orders',
  'merchant.dye.processOne': 'Process {{count}} order',
  'merchant.dye.processTwo': 'Process {{count}} orders',
  'merchant.dye.processFew': 'Process {{count}} orders',
  'merchant.dye.processMany': 'Process {{count}} orders',
  'merchant.dye.processOther': 'Process {{count}} orders',
  'merchant.dye.allClear': 'Nothing to prepare right now',
  'merchant.dye.viewStage': 'View “{{stage}}” orders',
  'merchant.dye.empty': 'Your first order will show up here, stage by stage.',
  'merchant.dashboard.updatedAt': 'Updated at {{time}}',
  'merchant.dashboard.figuresLabel': 'Key figures',
  'merchant.dashboard.alerts.checking': 'Checking…',
  'merchant.dashboard.alerts.allClear':
    'Nothing to report — stock, orders and coupons are all under control.',
  'merchant.dashboard.alerts.lowStockOne': '1 product to watch for stock',
  'merchant.dashboard.alerts.lowStockMany': '{{count}} products to watch for stock',
  'merchant.dashboard.alerts.lowStockOutOfStock': 'Including {{count}} out of stock.',
  'merchant.dashboard.alerts.lowStockBelowThreshold':
    'Available stock is below the alert threshold.',
  'merchant.dashboard.alerts.actionableOrdersOne': '1 order to process',
  'merchant.dashboard.alerts.actionableOrdersMany': '{{count}} orders to process',
  'merchant.dashboard.alerts.actionableOrdersDescription': 'Awaiting preparation or packing.',
  'merchant.dashboard.alerts.expiringCouponsOne': '1 coupon expires soon',
  'merchant.dashboard.alerts.expiringCouponsMany': '{{count}} coupons expire soon',
  'merchant.dashboard.alerts.expiringCouponsDescription': 'Within the next {{days}} days.',

  'merchant.dashboard.quickActions.title': 'Quick actions',
  'merchant.dashboard.quickActions.newProduct': 'New product',
  'merchant.dashboard.quickActions.newCoupon': 'New coupon',
  'merchant.dashboard.quickActions.viewOrders': 'View orders',

  'merchant.dashboard.salesTrend.loadError': 'Unable to load the sales trend',
  'merchant.dashboard.salesTrend.dayOrdersZero': '{{count}} orders',
  'merchant.dashboard.salesTrend.dayOrdersOne': '{{count}} order',
  'merchant.dashboard.salesTrend.dayOrdersTwo': '{{count}} orders',
  'merchant.dashboard.salesTrend.dayOrdersFew': '{{count}} orders',
  'merchant.dashboard.salesTrend.dayOrdersMany': '{{count}} orders',
  'merchant.dashboard.salesTrend.dayOrdersOther': '{{count}} orders',
  'merchant.dashboard.salesTrend.title': 'Sales trend',
  'merchant.dashboard.salesTrend.subtitle':
    'Last 14 days — confirmed orders, excluding cancellations',
  'merchant.dashboard.salesTrend.legendCurrency': 'Revenue (TND)',
  'merchant.dashboard.salesTrend.emptyTitle': 'No sales in this period',
  'merchant.dashboard.salesTrend.emptyDescription':
    'The chart will fill in as soon as your first non-cancelled order arrives.',
  'merchant.dashboard.salesTrend.chartAria': 'Chart of the sales trend over the last 14 days',
  'merchant.dashboard.salesTrend.totalLabel': 'Total for the period:',

  'merchant.dashboard.topProducts.title': 'Top products',
  'merchant.dashboard.topProducts.emptyTitle': 'No product sold yet',
  'merchant.dashboard.topProducts.emptyDescription':
    'The ranking will appear here as soon as there is a first non-cancelled order.',
  'merchant.dashboard.topProducts.soldCount': '{{count}} sold',

  // ---- merchant: orders ----
  'merchant.orders.title': 'Orders',
  'merchant.orders.subtitle': 'Track and process the orders you receive.',
  'merchant.orders.filterAllStatuses': 'All statuses',
  'merchant.orders.filterAria': 'Filter by status',
  'merchant.orders.loadError': 'Unable to load orders',
  'merchant.orders.accessDeniedDescription':
    'Your account does not have permission to manage orders.',
  'merchant.orders.paginationSummary': 'Orders {{from}}–{{to}} of {{total}}',
  'merchant.orders.paginationPageSuffix': ' · Page {{page}} of {{pages}}',
  'merchant.orders.prev': 'Previous',
  'merchant.orders.next': 'Next',
  'merchant.orders.table.colOrder': 'Order',
  'merchant.orders.table.colCustomer': 'Customer',
  'merchant.orders.table.colStatus': 'Status',
  'merchant.orders.table.colTotal': 'Total',
  'merchant.orders.table.colDate': 'Date',
  'merchant.orders.itemCount': '{{count}} item{{plural}}',
  'merchant.orders.actionProcessing': 'Mark in preparation',
  'merchant.orders.actionPacked': 'Move to delivery',
  'merchant.orders.completed': 'Completed',
  'merchant.orders.noActionNeeded': 'No action needed',
  'merchant.orders.updateModalTitle': 'Update the order',
  'merchant.orders.statusUpdated': 'Order status updated.',
  'merchant.orders.targetProcessing': 'Mark "In preparation"',
  'merchant.orders.targetPacked': 'Move to "Out for delivery"',
  'merchant.orders.targetCancel': 'Cancel the order',
  'merchant.orders.reasonLabel': 'Reason',

  // ---- merchant: store ----
  'merchant.store.subtitleNew': 'Set up your shop to start selling.',
  'merchant.store.subtitleExisting': "Manage your shop's public information.",
  'merchant.store.loadError': 'Unable to load the shop',
  'merchant.store.updated': 'Shop updated.',
  'merchant.store.nextStep.title': 'Shop saved',
  'merchant.store.nextStep.description':
    'Recommended next step: create your first category to organise your products.',
  'merchant.store.nextStep.cta': 'Create a category',
  'merchant.store.ownerOnlyNotice':
    'Only the shop owner can change this information. You can review the form below, but not save it.',
  'merchant.store.createSubmit': 'Create the shop',
  'merchant.store.saveSubmit': 'Save changes',

  'merchant.store.form.identity': 'Identity',
  'merchant.store.form.essentials': 'Essential information',
  'merchant.store.form.general': 'General information',
  'merchant.store.form.nameLabel': 'Shop name',
  'merchant.store.form.slugLabel': 'Slug',
  'merchant.store.form.slugHint': 'Used in the public URL — /shop/:slug.',
  'merchant.store.form.prefillNotice':
    'We prepared your shop from your account « {{name}} ». Check the name and the address, change them if you wish, then create the shop.',
  'merchant.store.form.slugManualHint':
    'This name has no Latin letters: choose an address using Latin letters, digits and hyphens (e.g. my-shop).',
  'merchant.store.form.slugTaken':
    'This address is already used by another shop. Choose a different one.',
  'merchant.store.form.descriptionLabel': 'Description',
  'merchant.store.form.logoUrlLabel': 'Logo',
  'merchant.store.form.logoUrlHint':
    'Shown in your shop header, home banner and footer. Ideal: a square image of at least 400 px.',
  'merchant.images.belowRecommended':
    '“{{name}}” is saved, but it is small ({{width}} × {{height}} px): it may look blurry once displayed. Recommended: at least {{recommendedWidth}} × {{recommendedHeight}} px.',
  'merchant.store.form.previewUnavailable':
    'The image is saved, but its preview could not be loaded.',
  'merchant.store.form.taglineLabel': 'Tagline',
  'merchant.store.form.taglineHint':
    'A short sentence shown under your shop name (160 characters max).',
  'merchant.store.form.story': 'Our story',
  'merchant.store.form.storyTitleLabel': 'Title',
  'merchant.store.form.storyTitleHint': 'Leave empty to show “Our story” (120 characters max).',
  'merchant.store.form.storyBodyLabel': 'Your story',
  'merchant.store.form.storyBodyHint':
    'Who are you, what do you sell, why do you exist? Separate paragraphs with a blank line (2,000 characters max, plain text).',
  'merchant.store.form.whatsappLabel': 'WhatsApp',
  'merchant.store.form.whatsappHint':
    'Include the country code, e.g. +216 22 123 456. A WhatsApp button appears on your shop.',
  'merchant.store.form.phoneLabel': 'Phone',
  'merchant.store.form.emailLabel': 'Email',
  'merchant.store.form.addressLabel': 'Address',
  'merchant.store.form.seo': 'Search engine optimization (SEO)',
  'merchant.store.form.seoTitleLabel': 'SEO title',
  'merchant.store.form.seoDescriptionLabel': 'SEO description',
  'merchant.store.form.stockDisplayPolicy': 'Stock display on the storefront',
  'merchant.store.form.stockDisplayPolicy.exact': 'Exact quantity',
  'merchant.store.form.stockDisplayPolicy.exact.hint':
    'Always show the real number of units available.',
  'merchant.store.form.stockDisplayPolicy.lowStockOnly': '"Only X left" once stock is low',
  'merchant.store.form.stockDisplayPolicy.lowStockOnly.hint':
    'The exact number only appears once stock is low or out — recommended.',
  'merchant.store.form.stockDisplayPolicy.hidden': 'Never show the quantity',
  'merchant.store.form.stockDisplayPolicy.hidden.hint':
    'Only the state (in stock / low stock / out of stock) is visible, never the number.',
  'merchant.store.form.deliveryFeeLabel': 'Delivery fee (TND)',
  'merchant.store.form.deliveryFeeHint':
    "Added to every order's subtotal. Leave at 0 to charge no delivery fee.",
  'merchant.store.form.branding': 'Visual identity',
  'merchant.store.form.brandingAdvanced': 'Advanced',
  'merchant.store.form.bannerUrlLabel': 'Home page banner',
  'merchant.store.form.bannerUrlHint':
    'Shown full-width at the top of your home page. Ideal: a wide image of at least 1600 × 600 px.',
  'merchant.store.form.colorPrimaryLabel': 'Primary color',
  'merchant.store.form.colorPrimaryHint': 'Dominates the home banner and brand elements.',
  'merchant.store.form.colorSecondaryLabel': 'Secondary color',
  'merchant.store.form.colorSecondaryHint': 'Used for supporting banners and sections.',
  'merchant.store.form.colorAccentLabel': 'Accent color',
  'merchant.store.form.colorAccentHint': 'Used for the main button, by default.',
  'merchant.store.form.colorTextLabel': 'Text color',
  'merchant.store.form.colorTextHint': 'Leave empty to keep the automatically computed text color.',
  'merchant.store.form.colorButtonLabel': 'Button color',
  'merchant.store.form.colorButtonHint': 'Falls back to the accent color when left empty.',
  'merchant.store.form.colorBadgeLabel': 'Badge color',
  'merchant.store.form.colorBadgeHint': 'Falls back to the secondary color when left empty.',
  'merchant.store.form.colorPromoLabel': 'Promotion color',
  'merchant.store.form.colorPromoHint': 'Falls back to the accent color when left empty.',
  'merchant.store.form.colorVariantLabel': 'Variant color',
  'merchant.store.form.colorVariantHint':
    "A variant's (size, color…) selection ring. Falls back to the accent color when left empty.",
  'merchant.store.form.colorReset': 'Reset',

  'merchant.store.health.title': 'Shop health',
  'merchant.store.health.storeConfigured': 'Shop configured',
  'merchant.store.health.storeConfiguredHint': 'Fill in your shop in the form above.',
  'merchant.store.health.categoriesPresent': 'Categories present',
  'merchant.store.health.categoriesPresentHint': 'Create at least one category.',
  'merchant.store.health.productsPresent': 'Products present',
  'merchant.store.health.productsPresentHint': 'Create at least one product.',
  'merchant.store.health.activeProductsHint': 'Publish at least one product (status "Active").',
  'merchant.store.health.variantsPresent': 'Variants present',
  'merchant.store.health.variantsPresentHint': 'Add at least one variant to a product.',
  'merchant.store.health.stockAvailableHint': 'Declare available stock on at least one variant.',

  'merchant.store.visibility.label': 'Shop status',
  'merchant.store.visibility.active': 'Active — publicly visible',
  'merchant.store.visibility.pending': 'Pending approval — not publicly visible yet',
  'merchant.store.visibility.suspended': 'Suspended — not publicly visible',
  'merchant.store.visibility.refused': 'Refused — not publicly visible',
  'merchant.share.refused.title': 'Your application was refused',
  'merchant.share.refused.text':
    'The platform did not approve this shop: it will not be visible to the public. Contact the platform support to find out more.',
  'merchant.store.visibility.publicCatalog': 'Public catalogue:',
  'merchant.share.readyTitle': 'Your shop is online',
  'merchant.share.myShop': 'My shop',
  'merchant.share.online': 'Online',
  'merchant.share.sendRow': 'Send',
  'merchant.share.trackedRow': 'Tracked link',
  'merchant.share.readySubtitle':
    'Share this link with your customers: on WhatsApp, Facebook, Telegram or anywhere else.',
  'merchant.share.linkLabel': 'Your shop public link',
  'merchant.share.copy': 'Copy link',
  'merchant.share.copied': 'Link copied',
  'merchant.share.copyFailed': 'Could not copy: select the link and copy it by hand.',
  'merchant.share.open': 'Open my shop',
  'merchant.share.newTab': '(opens in a new tab)',
  'merchant.share.shareOn': 'Share on',
  'merchant.share.whatsapp': 'WhatsApp',
  'merchant.share.facebook': 'Facebook',
  'merchant.share.telegram': 'Telegram',
  'merchant.share.message': 'Discover my shop {{name}}:',
  'merchant.share.noStore.title': 'Create your shop to get your public link',
  'merchant.share.noStore.text':
    "Fill in your shop's name and address: the link to share will appear here.",
  'merchant.share.noStore.cta': 'Create my shop',
  'merchant.share.pending.title': 'Your shop is waiting to be activated',
  'merchant.share.pending.text':
    'As soon as it is activated, your public link will appear here, ready to share.',
  'merchant.share.suspended.title': 'Your shop is suspended',
  'merchant.share.suspended.text':
    'It is no longer visible to the public. Contact the platform support to reactivate it.',
  'merchant.share.noProduct.title': 'Add your first product to put the shop online',
  'merchant.share.noProduct.text':
    'Your public link will appear here as soon as a product is visible in the catalogue.',
  'merchant.share.noProduct.cta': 'Add a product',
  'merchant.share.unpublished.title': 'Publish your catalogue to put the shop online',
  'merchant.share.unpublished.text':
    'Your products are still drafts or inactive. Set at least one to « Active »: your public link will appear here.',
  'merchant.share.unpublished.cta': 'See my products',

  // ---- merchant: categories ----
  'merchant.categories.subtitle':
    'Organize your catalogue into categories and subcategories, visible on your public shop.',
  'merchant.categories.newCategory': 'New category',
  'merchant.categories.loadError': 'Unable to load categories',
  'merchant.categories.accessDeniedDescription':
    'Your account does not have permission to manage the catalogue.',
  'merchant.categories.emptyTitle': 'No categories yet',
  'merchant.categories.emptyDescription':
    'Create your first category to start organizing your catalogue (e.g. Fashion, Electronics, Home).',
  'merchant.categories.editTitle': 'Edit category',
  'merchant.categories.createAction': 'Create',
  'merchant.categories.updated': 'Category updated.',
  'merchant.categories.created': 'Category created.',
  'merchant.categories.nextStep.title': 'Category created',
  'merchant.categories.nextStep.description': 'Recommended next step: create your first product.',
  'merchant.categories.nextStep.cta': 'Create a product',
  'merchant.categories.deleted': 'Category deleted.',
  'merchant.categories.deleteConfirm':
    'Delete category "{{name}}"? This action is irreversible.{{warning}}',
  'merchant.categories.deleteWarning':
    ' {{count}} product(s) are currently assigned to this category and will lose that assignment.',
  'merchant.categories.form.nameLabel': 'Name',
  'merchant.categories.form.slugHint': "Used in the category's public URL.",
  'merchant.categories.form.imageLabel': 'Image',
  'merchant.categories.form.imageHint': 'URL of an already-hosted image (no file upload here).',
  'merchant.categories.form.parentLabel': 'Parent category',
  'merchant.categories.form.parentHint': 'Leave empty for a root category.',
  'merchant.categories.form.activeCheckbox': 'Active category (visible on the public shop)',
  'merchant.categories.table.colParent': 'Parent',
  'merchant.categories.table.colProducts': 'Products',
  'merchant.categories.active': 'Active',
  'merchant.categories.inactive': 'Inactive',

  // ---- merchant: products ----
  'merchant.products.subtitle': "Manage your shop's catalogue.",
  'merchant.products.newProduct': 'New product',
  'merchant.products.loadError': 'Unable to load products',
  'merchant.products.emptyTitle': 'No products yet',
  'merchant.products.emptyDescription':
    'Create your first product to start building your catalogue.',
  'merchant.products.createAction': 'Create a product',
  'merchant.products.noMatchTitle': 'No product matches these filters',
  'merchant.products.noMatchDescription': 'Try a different search or reset the filters.',
  'merchant.products.table.colProduct': 'Product',
  'merchant.products.table.colPrice': 'Price',
  'merchant.products.table.colStock': 'Stock',
  'merchant.products.table.colCategory': 'Category',
  'merchant.products.table.colLastModified': 'Last modified',
  'merchant.products.table.colFeatured': 'Favourite',
  'merchant.products.featured.add': 'Feature this product',
  'merchant.products.featured.remove': 'Remove from favourites',
  'merchant.products.featured.hint':
    "Favourites (5 at most) are shown at the top of the shop's home page. The most recently featured product is shown large.",
  'merchant.products.featured.inactiveNote':
    "This product is not active: it will only appear among the shop's favourites once it is activated.",
  'merchant.products.featured.limitReached':
    'You already have {{limit}} favourites. Remove one before adding another.',
  'merchant.products.featured.error': 'This favourite could not be changed. Please try again.',
  'merchant.products.status.draft': 'Draft',
  'merchant.products.status.active': 'Active',
  'merchant.products.status.inactive': 'Inactive',
  'merchant.products.toolbar.searchPlaceholder': 'Search by name or SKU…',
  'merchant.products.toolbar.searchAria': 'Search a product',
  'merchant.products.toolbar.filterStatusAria': 'Filter by status',
  'merchant.products.toolbar.sortAria': 'Sort',
  'merchant.products.toolbar.statusAll': 'All statuses',
  'merchant.products.toolbar.sortUpdatedDesc': 'Last modified (newest)',
  'merchant.products.toolbar.sortUpdatedAsc': 'Last modified (oldest)',
  'merchant.products.toolbar.sortNameAsc': 'Name (A→Z)',
  'merchant.products.toolbar.sortNameDesc': 'Name (Z→A)',
  'merchant.products.toolbar.sortPriceAsc': 'Price (ascending)',
  'merchant.products.toolbar.sortPriceDesc': 'Price (descending)',
  'merchant.products.toolbar.sortStockAsc': 'Stock (out of stock first)',
  'merchant.products.toolbar.sortStockDesc': 'Stock (available first)',
  'merchant.products.stock.inStock': 'In stock',
  'merchant.products.stock.lowStock': 'Low stock',
  'merchant.products.stock.outOfStock': 'Out of stock',
  'merchant.products.stock.unavailable': 'Unavailable',
  'merchant.products.stock.units': '{{count}} unit{{plural}}',
  'merchant.products.create.title': 'New product',
  'merchant.products.create.subtitle': 'Add a product to your catalogue.',
  'merchant.products.create.submitLabel': 'Create the product',
  'merchant.products.createdToast': 'Product "{{name}}" created.',
  'merchant.products.nextStep.title': 'Product created successfully',
  'merchant.products.nextStep.description': 'Recommended next step: configure your inventory.',
  'merchant.products.nextStep.cta': 'Go to inventory',
  'merchant.products.updatedToast': 'Product updated.',
  'merchant.products.deleteConfirm': 'Delete this product? This action is irreversible.',
  'merchant.products.deletedToast': 'Product deleted.',
  'merchant.products.notFoundTitle': 'Product not found',
  'merchant.products.detailLoadError': 'Unable to load this product',
  'merchant.products.imagesHeading': 'Images',
  'merchant.products.imageUploaded': 'Image uploaded.',

  'merchant.products.form.generalInfo': 'General information',
  'merchant.products.form.nameLabel': 'Product name',
  'merchant.products.form.slugHint': "Used in the product's public URL.",
  'merchant.products.form.skuLabel': 'SKU',
  'merchant.products.form.categoryLabel': 'Category',
  'merchant.products.form.categoryLoading': 'Loading categories…',
  'merchant.products.form.categoryNone': 'No category',
  'merchant.products.form.shortDescriptionLabel': 'Short description',
  'merchant.products.form.longDescriptionLabel': 'Long description',
  'merchant.products.form.brandLabel': 'Brand',
  'merchant.products.form.priceAndStatus': 'Price and status',
  'merchant.products.form.priceLabel': 'Price (TND)',
  'merchant.products.form.compareAtPriceLabel': 'Compare-at price',
  'merchant.products.form.compareAtPriceHint': 'Comparison price shown struck through.',
  'merchant.products.form.statusDraftOption': 'Draft (not publicly visible)',
  'merchant.products.form.statusActiveOption': 'Active (visible in the public catalogue)',

  'merchant.products.images.sending': 'Uploading…',
  'merchant.products.images.error': 'Error',
  'merchant.products.images.uploadFailed': 'Upload failed.',
  'merchant.products.images.dropzone': 'Drag and drop an image, or click to browse',
  'merchant.products.images.formats': 'JPEG, PNG or WebP',
  'merchant.products.images.loadError': 'Unable to load images',
  'merchant.products.images.empty': 'No image uploaded for this product.',
  'merchant.products.images.loadErrorBadge': 'Failed to load',
  'merchant.products.images.position': 'Position {{n}}',

  'merchant.products.variants.title': 'Variants',
  'merchant.products.variants.add': 'Add a variant',
  'merchant.products.variants.createdToast': 'Variant "{{sku}}" created.',
  'merchant.products.variants.deleteConfirm': 'Delete this variant?',
  'merchant.products.variants.deletedToast': 'Variant deleted.',
  'merchant.products.variants.loadError': 'Unable to load variants',
  'merchant.products.variants.empty': 'No variant for this product',
  'merchant.products.variants.colAttributes': 'Attributes',
  'merchant.products.variants.colSpecificPrice': 'Specific price',
  'merchant.products.variants.skuLabel': 'Variant SKU',
  'merchant.products.variants.priceOverrideHint': "Leave empty to use the product's price.",
  'merchant.products.variants.attributesExample': '(e.g. color, size)',
  'merchant.products.variants.attributeKeyPlaceholder': 'Key (e.g. color)',
  'merchant.products.variants.attributeValuePlaceholder': 'Value (e.g. Black)',
  'merchant.products.variants.removeAttribute': 'Remove',
  'merchant.products.variants.addAttribute': 'Add an attribute',
  'merchant.products.variants.colStatus': 'Status',
  'merchant.products.variants.status.active': 'Active',
  'merchant.products.variants.status.inactive': 'Deactivated',
  'merchant.products.variants.activate': 'Activate',
  'merchant.products.variants.deactivate': 'Deactivate',
  'merchant.products.variants.activatedToast':
    'Variant activated — visible in the storefront again.',
  'merchant.products.variants.deactivatedToast':
    'Variant deactivated — hidden from the storefront, stock and history kept.',

  'merchant.products.options.title': 'Product options',
  'merchant.products.options.add': 'Add an option',
  'merchant.products.options.namePlaceholder': 'e.g. Size, Colour',
  'merchant.products.options.displayTypeLabel': 'Display type',
  'merchant.products.options.displayType.button': 'Buttons',
  'merchant.products.options.displayType.colorSwatch': 'Colour swatches',
  'merchant.products.options.rename': 'Rename',
  'merchant.products.options.delete': 'Delete option',
  'merchant.products.options.deleteConfirm':
    'Delete this option? Not possible while any variant still uses it.',
  'merchant.products.options.addValue': 'Add a value',
  'merchant.products.options.valuePlaceholder': 'e.g. 42, Red',
  'merchant.products.options.swatchColor': 'Swatch colour',
  // ---- merchant assistant (chatbot) ----
  'merchant.assistant.open': 'Open the assistant',
  'merchant.assistant.title': 'Assistant',
  'merchant.assistant.subtitle': 'Run your shop by conversation',
  'merchant.assistant.close': 'Close the assistant',
  'merchant.assistant.newConversation': 'New conversation',
  'merchant.assistant.unavailable':
    'The assistant is not configured on this server. Contact the platform administrator.',
  'merchant.assistant.welcome':
    'Hello! Tell me what you want to do: create a product, adjust stock, follow an order, create a coupon… I will ask you to confirm before any change.',
  'merchant.assistant.privacy':
    'Your messages and the shop data needed to answer are processed by an external AI service (Anthropic). Never type a password here.',
  'merchant.assistant.placeholder': 'Type your request…',
  'merchant.assistant.send': 'Send',
  'merchant.assistant.thinking': 'The assistant is thinking…',
  'merchant.assistant.operation.succeeded': 'succeeded',
  'merchant.assistant.operation.failed': 'failed (status {{status}})',
  'merchant.assistant.secret.hint':
    'Write it down now: it will never be shown again. It is not passed to the assistant.',
  'merchant.assistant.secret.copy': 'Copy',
  'merchant.assistant.secret.copied': 'Copied',
  'merchant.assistant.action.openPage': 'Open the page',
  'merchant.assistant.action.uploadPhoto': 'Choose a photo',
  'merchant.assistant.action.uploadLogo': 'Choose the logo',
  'merchant.assistant.action.uploadBanner': 'Choose the banner',
  'merchant.assistant.action.uploaded': 'Image uploaded.',
  'merchant.assistant.action.downloadInvoice': 'Download the invoice (PDF)',
  'merchant.assistant.pending.title': 'To confirm',
  'merchant.assistant.pending.confirm': 'Confirm',
  'merchant.assistant.pending.cancel': 'Cancel',
  'merchant.assistant.notice.refusal': 'The assistant cannot answer this request.',
  'merchant.assistant.notice.outputLimit': 'The answer was cut off; nothing was run.',
  'merchant.assistant.notice.stepLimit':
    'This request is too long to handle at once: split it into several steps.',
  'merchant.assistant.notice.conversationFull': 'This conversation is full: start a new one.',
  'merchant.assistant.notice.declined': 'Operation cancelled: nothing was changed.',
  'merchant.assistant.notice.conversationExpired':
    'This conversation has expired: the next message starts a new one.',
  'merchant.assistant.notice.nothingPending':
    'There is nothing left to confirm: this proposal was already handled or has expired.',
  'merchant.products.options.editValue': 'Edit',
  'merchant.products.options.deleteValue': 'Delete',
  'merchant.products.options.deleteValueConfirm':
    'Delete this value? Not possible while any variant still uses it.',
  'merchant.products.options.empty': 'No options defined yet — add one to generate variants.',
  'merchant.products.options.createdToast': 'Option "{{name}}" created.',
  'merchant.products.options.deletedToast': 'Option deleted.',
  'merchant.products.options.valueCreatedToast': 'Value "{{value}}" added.',
  'merchant.products.options.valueDeletedToast': 'Value deleted.',
  'merchant.products.options.autoGeneratedBadge': 'Auto-grouped',
  'merchant.products.options.loadError': 'Could not load options',

  'merchant.products.generator.title': 'Variant generator',
  'merchant.products.generator.openButton': 'Generate variants',
  'merchant.products.generator.selectValues': 'Select the values to combine for each option.',
  'merchant.products.generator.stepSelect': 'Select',
  'merchant.products.generator.stepPreview': 'Preview',
  'merchant.products.generator.previewTitle': 'Combinations to create',
  'merchant.products.generator.colCombination': 'Combination',
  'merchant.products.generator.colSku': 'SKU',
  'merchant.products.generator.colPrice': 'Specific price',
  'merchant.products.generator.noOptions':
    'Create at least one option with values before generating variants.',
  'merchant.products.generator.noCombinations': 'Select at least one value per option.',
  'merchant.products.generator.next': 'Preview',
  'merchant.products.generator.back': 'Back',
  'merchant.products.generator.generate': 'Create {{count}} variant(s)',
  'merchant.products.generator.generatedToast': '{{count}} variant(s) created.',
  'merchant.products.generator.duplicateSkuHint': 'Every SKU must be unique within the shop.',

  'merchant.products.bulkEdit.title': 'Bulk edit',
  'merchant.products.bulkEdit.openButton': 'Bulk edit',
  'merchant.products.bulkEdit.selectAll': 'Select all',
  'merchant.products.bulkEdit.selectedCount': '{{count}} variant(s) selected',
  'merchant.products.bulkEdit.noSelection': 'Select at least one variant.',
  'merchant.products.bulkEdit.priceModeLabel': 'Price',
  'merchant.products.bulkEdit.priceMode.none': 'Leave unchanged',
  'merchant.products.bulkEdit.priceMode.relative': 'Relative adjustment (+/-)',
  'merchant.products.bulkEdit.priceMode.absolute': 'New fixed price',
  'merchant.products.bulkEdit.priceAmountLabel': 'Amount',
  'merchant.products.bulkEdit.stockDeltaLabel': 'Stock adjustment',
  'merchant.products.bulkEdit.stockDeltaHint': 'Positive to restock, negative to remove.',
  'merchant.products.bulkEdit.thresholdLabel': 'Low-stock threshold',
  'merchant.products.bulkEdit.statusLabel': 'Status',
  'merchant.products.bulkEdit.status.unchanged': 'Leave unchanged',
  'merchant.products.bulkEdit.status.activate': 'Activate',
  'merchant.products.bulkEdit.status.deactivate': 'Deactivate',
  'merchant.products.bulkEdit.reasonLabel': 'Reason (optional)',
  'merchant.products.bulkEdit.apply': 'Apply',
  'merchant.products.bulkEdit.appliedToast': '{{count}} variant(s) updated.',

  'merchant.products.reviewBanner.title': 'Review your options',
  'merchant.products.reviewBanner.description':
    "We automatically grouped your existing variants' attributes into structured options. Review them whenever you get a chance — your variants stay sellable in the meantime.",
  'merchant.products.reviewBanner.dismiss': "I've reviewed them",
  'merchant.products.reviewBanner.dismissedToast': 'Options marked as reviewed.',

  // ---- merchant: inventory ----
  'merchant.inventory.subtitle': 'Available stock and alert thresholds for all your products.',
  'merchant.inventory.loadError': 'Unable to load inventory',
  'merchant.inventory.emptyTitle': 'No products to show',
  'merchant.inventory.emptyDescription': 'Add a product to start tracking its stock here.',
  'merchant.inventory.table.colStockAvailable': 'Available stock',
  'merchant.inventory.table.colAlertThreshold': 'Alert threshold',
  'merchant.inventory.none': 'None',
  'merchant.inventory.adjust': 'Adjust',
  'merchant.inventory.drawer.title': 'Adjust stock — {{name}}{{skuSuffix}}',
  'merchant.inventory.drawer.adjustmentLabel': 'Adjustment',
  'merchant.inventory.drawer.adjustmentHint':
    'Positive to add stock, negative to remove it (e.g. -5).',
  'merchant.inventory.drawer.reasonPlaceholder': 'E.g. supplier delivery',
  'merchant.inventory.drawer.thresholdLabel': 'Minimum alert threshold',
  'merchant.inventory.drawer.thresholdHint': 'Leave empty to keep the current threshold.',
  'merchant.inventory.drawer.apply': 'Apply',
  'merchant.inventory.adjustedToast':
    'Stock adjusted: {{available}} available, {{reserved}} reserved.',

  // ---- admin: shared layout ----
  'admin.sidebar.subtitle': 'Platform administration',
  'admin.topbar.label': 'Platform Admin space',

  // ---- admin: platform accounts (lot B1) ----
  'admin.nav.administrators': 'Administrators',
  'admin.nav.dashboard': 'Dashboard',
  'admin.dashboard.title': 'Dashboard',
  'admin.dashboard.subtitle':
    'The whole platform at a glance: activity, revenue, merchants, administrators and security.',
  'admin.dashboard.updatedNow': 'Updated just now',
  'admin.dashboard.updatedOneSecond': 'Updated 1 second ago',
  'admin.dashboard.updatedSeconds': 'Updated {{seconds}} seconds ago',
  'admin.dashboard.updatedMinutes': 'Updated {{minutes}} min ago',
  'admin.dashboard.refresh': 'Refresh',
  'admin.dashboard.refreshing': 'Refreshing…',
  'admin.dashboard.cacheNote': 'Figures are recomputed at most every 60 seconds.',
  'admin.dashboard.loading': 'Loading the dashboard…',
  'admin.dashboard.error.title': 'The dashboard could not be loaded',
  'admin.dashboard.error.forbidden': 'This dashboard is reserved to the superadmin.',
  'admin.dashboard.error.text':
    'Try again in a moment. If the problem persists, contact operations.',
  'admin.dashboard.retry': 'Retry',
  'admin.dashboard.empty.title': 'No data yet',
  'admin.dashboard.empty.text':
    'Figures will appear as soon as the platform has merchants and orders.',
  'admin.dashboard.chartLoadError': 'The charts are not available right now.',
  'admin.dashboard.section.financial': 'Financial',
  'admin.dashboard.section.merchants': 'Merchants',
  'admin.dashboard.section.actions': 'To handle',
  'admin.dashboard.section.trends': 'Trends',
  'admin.dashboard.section.admins': 'Administrators',
  'admin.dashboard.section.quality': 'Data quality',
  'admin.dashboard.section.security': 'Security',
  'admin.dashboard.section.products': 'Products',
  'admin.dashboard.section.topMerchants': 'Top merchants by revenue',
  'admin.dashboard.fin.salesTotal': 'Total sales',
  'admin.dashboard.fin.revenueToday': 'Revenue today',
  'admin.dashboard.fin.revenueMonth': 'Revenue this month',
  'admin.dashboard.fin.revenueYear': 'Revenue this year',
  'admin.dashboard.fin.averageBasket': 'Average basket',
  'admin.dashboard.fin.ordersTotal': 'Orders (total)',
  'admin.dashboard.fin.ordersToday': 'Orders today',
  'admin.dashboard.fin.excludesCancelled': 'Cancelled orders excluded',
  'admin.dashboard.fin.cancelled': '{{count}} cancelled order(s) not counted',
  'admin.dashboard.mer.total': 'Total merchants',
  'admin.dashboard.mer.active': 'Active',
  'admin.dashboard.mer.pending': 'Pending',
  'admin.dashboard.mer.suspended': 'Suspended',
  'admin.dashboard.mer.unassigned': 'Unassigned',
  'admin.dashboard.mer.openList': 'Open the list',
  'admin.dashboard.mer.withStore': '{{count}} with an online shop',
  'admin.dashboard.mer.refused': '{{count}} refused',
  'admin.dashboard.adm.total': 'Administrators',
  'admin.dashboard.adm.active': 'Active',
  'admin.dashboard.adm.disabled': 'Disabled',
  'admin.dashboard.adm.topTitle': 'Top 5 administrators by merchants managed',
  'admin.dashboard.adm.allTitle': 'All administrators',
  'admin.dashboard.adm.showAll': 'Show all {{count}} administrators',
  'admin.dashboard.adm.showTop': 'Show the top 5',
  'admin.dashboard.adm.capped':
    'The {{shown}} administrators managing the most shops are listed, out of {{total}} in total.',
  'admin.dashboard.adm.cappedLink': 'See all administrators',
  'admin.dashboard.adm.colAdmin': 'Administrator',
  'admin.dashboard.adm.colMerchants': 'Merchants',
  'admin.dashboard.adm.colActive': 'Active',
  'admin.dashboard.adm.colPending': 'Pending',
  'admin.dashboard.adm.colSuspended': 'Suspended',
  'admin.dashboard.adm.colRevenue': 'Revenue',
  'admin.dashboard.adm.colOrders': 'Orders',
  'admin.dashboard.adm.none': 'No platform administrator is registered.',
  'admin.dashboard.adm.disabledBadge': 'Disabled',
  'admin.dashboard.qua.neverSignedIn': 'Merchants never signed in',
  'admin.dashboard.qua.neverSignedInHint': 'The owner has never opened their space yet.',
  'admin.dashboard.qua.provisionalExpired': 'Provisional passwords expired',
  'admin.dashboard.qua.provisionalExpiredHint': 'To be renewed by the manager or a superadmin.',
  'admin.dashboard.qua.withoutOwner': 'Shops without an owner',
  'admin.dashboard.qua.withoutOwnerHint': 'No active owner account attached.',
  'admin.dashboard.qua.withoutProduct': 'Shops without a product',
  'admin.dashboard.qua.withoutProductHint': 'Refused shops excluded.',
  'admin.dashboard.sec.superadmins': 'Superadmins',
  'admin.dashboard.sec.platformAdmins': 'Platform admins',
  'admin.dashboard.sec.logins': 'Sign-ins today',
  'admin.dashboard.sec.loginsHint': 'of which platform accounts: {{count}}',
  'admin.dashboard.sec.failed': 'Failed attempts today',
  'admin.dashboard.sec.failedHint':
    'of which platform accounts: {{count}} · throttled: {{throttled}}',
  'admin.dashboard.sec.locked': 'Locked accounts',
  'admin.dashboard.sec.promotions': 'Promotions pending',
  'admin.dashboard.act.unassigned': 'Unassigned shops',
  'admin.dashboard.act.unassignedHint': 'To assign to an administrator before any approval.',
  'admin.dashboard.act.pendingOver7d': 'Shops pending > 7 days',
  'admin.dashboard.act.pendingOver7dHint': 'Created over seven days ago, still to approve.',
  'admin.dashboard.act.reassignments': 'Recent reassignments',
  'admin.dashboard.act.reassignmentsHint': '{{last7d}} in 7 days · {{last30d}} in 30 days',
  'admin.dashboard.act.toHandleTitle': 'Shops to handle',
  'admin.dashboard.act.toHandleTotal': '{{shown}} shown of {{total}}',
  'admin.dashboard.act.colShop': 'Shop',
  'admin.dashboard.act.colStatus': 'Status',
  'admin.dashboard.act.colReason': 'To do',
  'admin.dashboard.act.colAge': 'Age',
  'admin.dashboard.act.colManager': 'Manager',
  'admin.dashboard.act.reason.unassigned': 'Assign to an administrator',
  'admin.dashboard.act.reason.provisional_expired': 'Renew the provisional password',
  'admin.dashboard.act.reason.pending_over_7d': 'Approve or refuse (pending for over 7 days)',
  'admin.dashboard.act.ageDays': '{{days}} d',
  'admin.dashboard.act.noneToHandle': 'Nothing to handle: every shop is assigned and up to date.',
  'admin.dashboard.act.unnamed': 'Unnamed',
  'admin.dashboard.act.noManager': 'None',
  'admin.dashboard.act.recentTitle': 'Latest reassignments',
  'admin.dashboard.act.recentRow': '{{name}} → {{admin}}',
  'admin.dashboard.act.recentNone': 'No recent reassignment.',
  'admin.dashboard.act.openPortfolios': 'Open the portfolios',
  'admin.dashboard.trend.sales': 'Sales',
  'admin.dashboard.trend.orders': 'Orders',
  'admin.dashboard.trend.registrations': 'New merchants',
  'admin.dashboard.trend.currency': 'TND',
  'admin.dashboard.trend.range': 'Period',
  'admin.dashboard.trend.r7': '7 days',
  'admin.dashboard.trend.r30': '30 days',
  'admin.dashboard.trend.r90': '90 days',
  'admin.dashboard.trend.r12m': '12 months',
  'admin.dashboard.trend.aria': '{{title}}: {{total}} over the period, from {{from}} to {{to}}',
  'admin.dashboard.trend.empty': 'No data over the period.',
  'admin.dashboard.trend.peak': 'Peak: {{value}} on {{date}}',
  'admin.dashboard.trend.tableCaption': 'Data of the « {{title}} » chart',
  'admin.dashboard.trend.colPeriod': 'Period',
  'admin.dashboard.pro.total': 'Products',
  'admin.dashboard.pro.active': 'Active',
  'admin.dashboard.pro.outOfStock': 'Out of stock',
  'admin.dashboard.pro.topTitle': 'Top products sold',
  'admin.dashboard.pro.colProduct': 'Product',
  'admin.dashboard.pro.colMerchant': 'Merchant',
  'admin.dashboard.pro.colQuantity': 'Quantity',
  'admin.dashboard.pro.colRevenue': 'Revenue',
  'admin.dashboard.pro.none': 'No product sold yet.',
  'admin.dashboard.top.colMerchant': 'Merchant',
  'admin.dashboard.top.colRevenue': 'Revenue',
  'admin.dashboard.top.colOrders': 'Orders',
  'admin.dashboard.top.colManager': 'Manager',
  'admin.dashboard.top.none': 'No sales yet.',
  'admin.accounts.title': 'Platform administrators',
  'admin.accounts.subtitle':
    'Who holds power on the platform. Create an administrator, remove a role, disable an account, and promote a superadmin with two people.',
  'admin.accounts.tab.label': 'Administrator sections',
  'admin.accounts.tab.accounts': 'Accounts',
  'admin.accounts.tab.create': 'Create an administrator',
  'admin.accounts.tab.promotions': 'Superadmin promotions',
  'admin.accounts.tab.lookup': 'Account memberships',
  'admin.accounts.reasonPlaceholder': 'E.g. quarterly review of administrator access',
  'admin.accounts.reasonRequiredDescription':
    'State why you are viewing this list: the access is audited.',
  'admin.accounts.loadError': 'Could not load the accounts.',
  'admin.accounts.empty': 'No platform account.',
  'admin.accounts.table.caption': 'Platform accounts',
  'admin.accounts.table.colAccount': 'Account',
  'admin.accounts.table.colRole': 'Role',
  'admin.accounts.table.colStatus': 'Status',
  'admin.accounts.table.colLastLogin': 'Last sign-in',
  'admin.accounts.table.neverSignedIn': 'Never signed in',
  'admin.accounts.role.superadmin': 'Superadmin',
  'admin.accounts.role.platform_admin': 'Administrator',
  'admin.accounts.status.active': 'Active',
  'admin.accounts.status.disabled': 'Disabled',
  'admin.accounts.status.locked': 'Locked',
  'admin.accounts.provisionalPending': 'Provisional password pending',
  'admin.accounts.you': 'You',
  'admin.accounts.action.revoke': 'Remove role',
  'admin.accounts.action.disable': 'Disable',
  'admin.accounts.action.enable': 'Re-enable',
  'admin.accounts.revokeTitle': 'Remove the platform role',
  'admin.accounts.revokeWarning':
    'The account loses all platform power and its sessions end. The account itself is not deleted. The last superadmin can never be removed. An administrator who still manages merchants can be neither removed nor disabled: transfer the portfolio first (Portfolios menu).',
  'admin.accounts.disableTitle': 'Disable the account',
  'admin.accounts.disableWarning':
    'The person can no longer sign in and their sessions end. Reversible. The last active superadmin can never be disabled. An administrator who still manages merchants can be neither removed nor disabled: transfer the portfolio first (Portfolios menu).',
  'admin.accounts.enableTitle': 'Re-enable the account',
  'admin.accounts.updatedToast': 'Account updated.',
  'admin.accounts.create.title': 'Create an administrator',
  'admin.accounts.create.note':
    'The new account gets the Administrator role, never Superadmin (a superadmin is promoted by two people). A provisional password is generated: it is shown only once.',
  'admin.accounts.create.emailLabel': 'New administrator email address',
  'admin.accounts.create.displayNameLabel': 'Display name',
  'admin.accounts.create.submit': 'Create the administrator',
  'admin.accounts.create.success': 'Administrator created.',
  'admin.accounts.create.createAnother': 'Create another administrator',
  'admin.accounts.create.handover':
    'Hand this password over in person or through a safe channel. It expires, and the person must choose a new one at first sign-in.',
  'admin.promotions.title': 'Promotion to superadmin',
  'admin.promotions.subtitle':
    'Two different superadmins: one requests, the other confirms. At most three superadmins. A request expires.',
  'admin.promotions.requestTitle': 'Request a promotion',
  'admin.promotions.targetLabel': 'Administrator to promote',
  'admin.promotions.targetPlaceholder': 'Choose an active administrator',
  'admin.promotions.noCandidates': 'No active administrator to promote.',
  'admin.promotions.requestSubmit': 'Request the promotion',
  'admin.promotions.requestedToast': 'Request sent: another superadmin must confirm it.',
  'admin.promotions.confirmedToast': 'Promotion confirmed.',
  'admin.promotions.rejectedToast': 'Request rejected.',
  'admin.promotions.cancelledToast': 'Request cancelled.',
  'admin.promotions.listTitle': 'Recent requests (30 days)',
  'admin.promotions.empty': 'No recent request.',
  'admin.promotions.table.caption': 'Promotion requests',
  'admin.promotions.table.colTarget': 'Account to promote',
  'admin.promotions.table.colRequestedBy': 'Requested by',
  'admin.promotions.table.colStatus': 'Status',
  'admin.promotions.table.colExpires': 'Expires',
  'admin.promotions.status.pending': 'Pending',
  'admin.promotions.status.confirmed': 'Confirmed',
  'admin.promotions.status.rejected': 'Rejected',
  'admin.promotions.status.cancelled': 'Cancelled',
  'admin.promotions.status.expired': 'Expired',
  'admin.promotions.action.confirm': 'Confirm',
  'admin.promotions.action.reject': 'Reject',
  'admin.promotions.action.cancel': 'Cancel my request',
  'admin.promotions.confirmTitle': 'Confirm the promotion',
  'admin.promotions.confirmWarning':
    'The account will become a superadmin and its sessions will end. You confirm as the second superadmin: you cannot confirm your own request.',
  'admin.promotions.rejectTitle': 'Reject the request',
  'admin.promotions.cancelTitle': 'Cancel the request',
  'admin.promotions.waitingForOther': 'Waiting for another superadmin',
  'errors.platformAccountSelf': 'You cannot do this to your own account.',
  'errors.lastSuperadmin': 'The last active superadmin can be neither removed nor disabled.',
  'errors.accountStatusUnchanged': 'The account is already in that state.',
  'errors.accountHasMerchants':
    'This administrator still manages merchants: transfer the portfolio first.',
  'errors.managerRequired': 'Name the administrator who will manage this merchant.',
  'errors.managerForbidden': 'Only a superadmin can give a merchant to another administrator.',
  'errors.managerInvalid': 'The chosen manager is not an active platform administrator.',
  'errors.portfolioTargetInvalid':
    'The chosen administrator is not an active platform administrator.',
  'errors.portfolioUnchanged': 'These merchants are already managed by that administrator.',
  'errors.portfolioSameAdmin': 'The portfolio cannot be transferred to its own administrator.',
  'errors.portfolioEmpty': 'This administrator manages no merchant.',
  'errors.suspendedBySuperadmin':
    'This merchant was suspended by a superadmin: only a superadmin can reactivate it.',
  'errors.ownerInUse':
    'The owner has already used the account: only a superadmin can reset the password now.',
  'errors.promotionSelf': 'Nobody promotes or confirms their own promotion.',
  'errors.promotionTargetNotEligible':
    'Only an active administrator can be promoted to superadmin.',
  'errors.superadminCapReached': 'There can be at most 3 superadmins.',
  'errors.noSecondSuperadmin':
    'A promotion needs a second superadmin to confirm it: use the two-person operator procedure.',
  'errors.promotionAlreadyPending': 'A promotion is already waiting for this account.',
  'errors.promotionNotPending': 'This request has already been decided.',
  'errors.promotionExpired': 'This request has expired.',
  'errors.promotionSamePerson':
    'A different superadmin must confirm: nobody confirms their own request.',

  // ---- admin: access and passwords (lot B2) ----
  'admin.nav.access': 'Access and passwords',
  'admin.access.title': 'Access and passwords',
  'admin.access.subtitle':
    'Help an account without touching the database: locked accounts, provisional passwords, sessions. Every look-up is audited.',
  'admin.access.tab.label': 'Access and passwords sections',
  'admin.access.tab.locked': 'Locked accounts',
  'admin.access.tab.provisional': 'Provisional passwords',
  'admin.access.tab.lookup': 'Find an account',
  'admin.access.kind.superadmin': 'Superadmin',
  'admin.access.kind.platform_admin': 'Administrator',
  'admin.access.kind.shop': 'Shop account',
  'admin.access.loadError': 'Could not load the list.',
  'admin.access.locked.empty': 'No locked account.',
  'admin.access.locked.caption': 'Locked accounts',
  'admin.access.col.account': 'Account',
  'admin.access.col.kind': 'Type',
  'admin.access.col.failures': 'Failures',
  'admin.access.col.lockedUntil': 'Locked until',
  'admin.access.action.unlock': 'Unlock',
  'admin.access.unlockTitle': 'Unlock the account',
  'admin.access.unlockWarning':
    'The failure counter is reset and the person can sign in again at once. Check who they are first.',
  'admin.access.unlockedToast': 'Account unlocked.',
  'admin.access.provisional.empty': 'No provisional password pending.',
  'admin.access.provisional.caption': 'Provisional passwords pending',
  'admin.access.col.expires': 'Expires',
  'admin.access.provisional.expired': 'Expired: to be renewed',
  'admin.access.provisional.valid': 'Pending',
  'admin.access.lookup.emailLabel': 'Account email address',
  'admin.access.lookup.submit': 'Find',
  'admin.access.lookup.notFound': 'No account with this address.',
  'admin.access.lookup.reasonFirst': 'First state the consultation reason (audited) above.',
  'admin.access.lookup.status': 'Status',
  'admin.access.lookup.lastLogin': 'Last sign-in',
  'admin.access.lookup.locked': 'Locked until',
  'admin.access.lookup.provisional': 'Provisional password pending',
  'admin.access.sessions.title': 'Account sessions',
  'admin.access.sessions.caption': 'Sessions',
  'admin.access.sessions.empty': 'No session.',
  'admin.access.sessions.retention':
    'The IP address and the browser are kept for 90 days, then erased.',
  'admin.access.sessions.colOpened': 'Opened',
  'admin.access.sessions.colLastUsed': 'Last activity',
  'admin.access.sessions.colIp': 'IP address',
  'admin.access.sessions.colBrowser': 'Browser',
  'admin.access.sessions.colState': 'State',
  'admin.access.sessions.active': 'Active',
  'admin.access.sessions.ended': 'Ended',
  'admin.access.sessions.noMetadata': 'Erased (over 90 days)',
  'admin.access.sessions.reason.logout': 'Signed out',
  'admin.access.sessions.reason.logout_all': 'Signed out everywhere',
  'admin.access.sessions.reason.token_reuse_detected': 'Token theft detected',
  'admin.access.sessions.reason.password_changed': 'Password changed',
  'admin.access.sessions.reason.expired': 'Expired',
  'admin.access.sessions.reason.role_changed': 'Role changed',
  'admin.access.sessions.reason.account_disabled': 'Account disabled',
  'admin.access.sessions.reason.admin_revoked': 'Ended by a superadmin',
  'admin.access.sessions.revokeAll': 'End all sessions',
  'admin.access.sessions.revokeTitle': 'End all the sessions of the account',
  'admin.access.sessions.revokeWarning':
    'The person is signed out everywhere and must sign in again. Not possible on your own account.',
  'admin.access.sessions.revokedToast': '{{count}} session(s) ended.',
  'admin.access.reset.action': 'Reset the password',
  'admin.access.reset.title': 'New provisional password',
  'admin.access.reset.warning':
    'The current password stops working, the account sessions end and the lock is lifted. The person must choose their own password at first sign-in. At most 3 resets a day.',
  'admin.access.reset.methodLabel': "How was the person's identity checked?",
  'admin.access.reset.methodHint':
    'Outside the platform: there is no e-mail, this is the real protection. It is recorded in the audit trail.',
  'admin.access.reset.method.phone_call': 'Phone call (known number)',
  'admin.access.reset.method.video_call': 'Video call',
  'admin.access.reset.method.in_person': 'In person',
  'admin.access.reset.methodPlaceholder': 'Choose a method',
  'admin.access.reset.submit': 'Generate a provisional password',
  'admin.access.reset.superadminNote':
    'A superadmin is never reset through the application: two-person break-glass procedure (see the runbook).',
  'admin.access.reset.shopNote': "A shop owner's password is reset from the merchant list.",
  'errors.superadminNotResettable':
    'A superadmin is never reset through the application: use the break-glass procedure.',
  'errors.accountDisabled': 'The account is disabled.',
  'errors.accountNotLocked': 'The account is not locked.',
  'errors.insufficientStock':
    'Not enough stock: this removal would take the available quantity below zero.',
  'errors.storageUnavailable':
    'The image service is temporarily unavailable. Your file is not the problem: please try again shortly.',

  // ---- shopper side: cart, checkout, coupon, order lookup ----
  'errors.cartNotFound':
    'Your cart could not be found or has expired. Go back to the shop to start again.',
  'errors.cartItemNotFound': 'This item is no longer in your cart.',
  'errors.cartEmpty': 'Your cart is empty.',
  'errors.productNotAvailable': 'This product is no longer available.',
  'errors.stockExceeded': 'There is not enough stock for this quantity.',
  'errors.variantRequired': 'Choose an option (size, colour…) before adding this product.',
  'errors.variantNotFound': 'This option does not exist for this product.',
  'errors.couponNotFound': 'This promo code does not exist.',
  'errors.couponNotYetValid': 'This promo code is not valid yet.',
  'errors.couponExpired': 'This promo code has expired.',
  'errors.couponMinimumNotMet': 'Your order does not reach the minimum amount for this promo code.',
  'errors.couponUsageLimitReached': 'This promo code has reached its usage limit.',
  'errors.orderNotFound': 'No order matches this number and phone number.',

  // ---- account and security (lot B3) ----
  'account.nav': 'My account',
  'account.title': 'My account',
  'account.subtitle': "Your sign-ins, your sessions and your account's security history.",
  'account.changePassword': 'Change my password',
  'account.lastSignIn.title': 'Last sign-in',
  'account.lastSignIn.value': '{{date}}, from {{ip}}',
  'account.lastSignIn.valueNoIp': '{{date}}',
  'account.lastSignIn.none': 'This is your first sign-in.',
  'account.lastSignIn.notice': 'Last sign-in: {{date}}',
  'account.lastSignIn.dismiss': 'Dismiss',
  'account.lastSignIn.hint': "Wasn't it you? Change your password and end the other sessions.",
  'account.loadError': 'Could not load the history.',
  'account.sessions.title': 'My sessions',
  'account.sessions.caption': 'Sessions',
  'account.sessions.retention': 'The IP address and the browser are kept for 90 days, then erased.',
  'account.sessions.colOpened': 'Opened',
  'account.sessions.colLastUsed': 'Last activity',
  'account.sessions.colIp': 'IP address',
  'account.sessions.colBrowser': 'Browser',
  'account.sessions.colState': 'State',
  'account.sessions.current': 'This session',
  'account.sessions.active': 'Active',
  'account.sessions.ended': 'Ended',
  'account.sessions.erased': 'Erased (over 90 days)',
  'account.sessions.empty': 'No session.',
  'account.sessions.revokeOthers': 'Sign me out everywhere else',
  'account.sessions.revokeOthersHint':
    'Ends all your other sessions and keeps this one. Do it if you think another device has access to your account.',
  'account.sessions.revokedToast': '{{count}} other session(s) ended.',
  'account.events.title': 'Security history',
  'account.events.caption': 'Security events',
  'account.events.colWhen': 'When',
  'account.events.colEvent': 'Event',
  'account.events.colIp': 'IP address',
  'account.events.empty': 'No event.',
  'account.event.auth.login_succeeded': 'Signed in',
  'account.event.auth.login_failed': 'Failed sign-in attempt',
  'account.event.auth.account_locked': 'Account locked (too many failures)',
  'account.event.auth.password_changed': 'Password changed',
  'account.event.auth.password_change_failed': 'Password change refused',
  'account.event.auth.password_reset_completed': 'Password reset',
  'account.event.auth.sessions_revoked': 'Sessions ended',
  'account.event.auth.reauth_failed': 'Password confirmation refused',
  'account.event.auth.temporary_password_expired': 'Provisional password expired',
  'account.event.unknown': 'Security event',
  'admin.nav.security': 'Security and audit',
  'admin.security.title': 'Security and audit',
  'admin.security.subtitle':
    'The audit journal, the sign-ins and the alerts. Read only: the journal can be neither edited nor erased, and every look-up is itself recorded in it.',
  'admin.security.tab.label': 'Security and audit sections',
  'admin.security.tab.audit': 'Audit journal',
  'admin.security.tab.events': 'Sign-ins',
  'admin.security.tab.alerts': 'Alerts',
  'admin.security.reasonPlaceholder': 'E.g. monthly security review',
  'admin.security.reasonRequired':
    'State why you are reading the journal: the access is itself audited.',
  'admin.security.loadError': 'Could not load the data.',
  'admin.security.audit.filterAction': 'Action (prefix)',
  'admin.security.audit.filterActionPlaceholder': 'e.g. superadmin. or auth.login',
  'admin.security.audit.filterTarget': 'Target (identifier)',
  'admin.security.audit.filterActor': 'Actor (UUID)',
  'admin.security.audit.filterTenant': 'Organisation (UUID)',
  'admin.security.audit.filterFrom': 'From',
  'admin.security.audit.filterTo': 'To (exclusive)',
  'admin.security.audit.apply': 'Apply',
  'admin.security.audit.caption': 'Audit journal',
  'admin.security.audit.colWhen': 'When',
  'admin.security.audit.colActor': 'Actor',
  'admin.security.audit.colAction': 'Action',
  'admin.security.audit.colTarget': 'Target',
  'admin.security.audit.colIp': 'IP address',
  'admin.security.audit.colDetails': 'Details',
  'admin.security.audit.anonymous': 'Anonymous',
  'admin.security.audit.empty': 'No entry for these filters.',
  'admin.security.audit.loadMore': 'Load older entries',
  'admin.security.audit.export': 'Export as CSV',
  'admin.security.audit.exportHint':
    'At most 5,000 rows, with the filters and the reason above. The export is itself recorded in the journal.',
  'admin.security.audit.exported': 'Export downloaded.',
  'admin.security.audit.exportError': 'The export failed.',
  'admin.security.events.hours': 'Period',
  'admin.security.events.hoursOption': '{{hours}} h',
  'admin.security.events.totals': 'Totals',
  'admin.security.events.failedByIp': 'Failures by IP address',
  'admin.security.events.failedByIdentifier': 'Failures by identifier (truncated fingerprint)',
  'admin.security.events.empty': 'Nothing in this period.',
  'admin.security.events.colEvent': 'Event',
  'admin.security.events.colCount': 'Count',
  'admin.security.events.colKey': 'Key',
  'admin.security.alerts.empty': 'No alert: nothing exceeds the thresholds.',
  'admin.security.alerts.note':
    'Computed at each look-up from the journal and the sessions: nothing is stored or sent (there is no e-mail).',
  'admin.security.severity.critical': 'Critical',
  'admin.security.severity.warning': 'Warning',
  'admin.security.severity.info': 'Information',
  'admin.security.alert.login_failures_spike': '{{count}} failed sign-ins in the last hour',
  'admin.security.alert.login_failures_single_ip':
    '{{count}} failed sign-ins from one address in the last hour',
  'admin.security.alert.accounts_locked': '{{count}} account(s) locked in the last 24 h',
  'admin.security.alert.break_glass_executed': 'Break-glass executed ({{count}}) in the last 24 h',
  'admin.security.alert.last_superadmin_override':
    '"Last superadmin" override used ({{count}}) in the last 24 h',
  'admin.security.alert.superadmin_promoted': 'Superadmin promotion ({{count}}) in the last 24 h',
  'admin.security.alert.platform_role_revoked':
    'Platform role removed ({{count}}) in the last 24 h',
  'admin.security.alert.platform_account_disabled':
    'Platform account disabled ({{count}}) in the last 24 h',
  'admin.security.alert.platform_admin_created':
    'Administrator created ({{count}}) in the last 24 h',
  'admin.security.alert.owner_password_reissued':
    'Owner password reset ({{count}}) in the last 24 h',
  'admin.security.alert.admin_password_reissued':
    'Administrator password reset ({{count}}) in the last 24 h',
  'admin.security.alert.promotion_pending':
    '{{count}} superadmin promotion(s) waiting for confirmation',
  'admin.security.alert.superadmin_new_address':
    '{{count}} superadmin sign-in(s) from a never-seen address (24 h)',
  'admin.security.alert.no_second_superadmin':
    'Fewer than two active superadmins: the application cannot promote anybody',
  'admin.security.alert.unknown': 'Security alert',

  // ---- admin: nav ----
  'admin.nav.merchants': 'Merchants',

  // ---- admin: auth ----
  'admin.auth.loginTitle': 'Log in',
  'admin.auth.loginSubtitle': 'Access platform administration.',
  'admin.auth.loginButton': 'Log in',
  'admin.auth.passwordLabel': 'Password',

  // ---- admin: shared reason field ----
  'admin.common.reasonLabel': 'Reason (minimum 10 characters, required and audited)',
  'admin.common.reasonConsultationLabel': 'Reason for consultation (optional)',
  'admin.common.reasonRequiredTitle': 'Reason required',

  // ---- admin: merchants ----
  'admin.merchants.subtitle': "Approval queue and status management for the platform's merchants.",
  'admin.merchants.reasonPlaceholder': 'E.g.: processing the daily approval queue',
  'admin.merchants.filterStatusAria': 'Filter by status',
  'admin.merchants.reasonRequiredDescription':
    'Enter a reason for consultation (minimum 10 characters) to load the list — every read in this domain is audited.',
  'admin.merchants.loadError': 'Unable to load merchants',
  'admin.merchants.accessDeniedDescription':
    'Your account does not hold the Platform Admin role, or two-factor verification was not presented.',
  'admin.merchants.emptyForFilter': 'No merchant for this filter',
  'admin.merchants.paginationSummary': '{{from}}–{{to}} of {{total}}',
  'admin.merchants.table.colLegalName': 'Legal name',
  'admin.merchants.table.colCreatedAt': 'Created on',
  'admin.merchants.action.approve': 'Approve',
  'admin.merchants.action.suspend': 'Suspend',
  'admin.merchants.action.reactivate': 'Reactivate',
  'admin.merchants.updateModalTitle': "Update the merchant's status",
  'admin.merchants.updatedToast': "Merchant's status updated.",
  'admin.merchants.noEmailHint':
    'No email is sent to the merchant for this change: tell them directly if needed.',

  // ---- admin: platform roles ----
  'admin.roles.subtitle': "Look up an account's memberships (audited).",
  'admin.roles.lookupSectionTitle': "Look up a user's memberships",
  'admin.roles.noMembershipsTitle': 'No membership for this user',
  'admin.roles.membershipsTableCaption': 'Memberships',
  'admin.roles.table.colTenant': 'Shop (tenant)',
  'admin.roles.userIdLabel': 'User ID (UUID)',
  'admin.roles.lookupButton': 'Look up memberships',

  // ---- admin: organisation provisioning ----
  'admin.nav.organisations': 'New merchant',
  'admin.organisations.title': 'Create a merchant account',
  'admin.organisations.subtitle':
    "Creates the shop and its owner's account. A provisional password is generated and shown only once: you hand it to the merchant, who must choose a new one at their first sign-in. No email is sent.",
  'admin.organisations.emailLabel': "Owner's email address",
  'admin.organisations.legalNameLabel': 'Legal name',
  'admin.organisations.organisationActiveLabel': 'Approve the shop immediately',
  'admin.organisations.organisationActiveHint':
    'Unchecked, the shop joins the usual approval queue.',
  'admin.organisations.reasonLabel': 'Reason (audited)',
  'admin.common.confirmPasswordLabel': 'Your password (confirmation)',
  'admin.common.confirmPasswordHint':
    'Sensitive action: type your own password again. It is neither stored nor displayed.',
  'admin.common.confirmPasswordRequired': 'Enter your password to confirm.',
  'admin.merchants.action.refuse': 'Refuse',
  'admin.merchants.action.resetOwner': 'Owner password',
  'admin.merchants.statusRefused': 'Refused',
  'admin.nav.portfolios': 'Portfolios',
  'admin.merchants.scopeAdminInfo':
    'You only see the merchants of your portfolio: the ones you created or that a superadmin assigned to you.',
  'admin.merchants.scopeSuperInfo':
    'You see every merchant of the platform, assigned or not. You can assign them to an administrator; only the managing administrator approves or refuses a shop.',
  'admin.merchants.portfolioEmptyTitle': 'Your portfolio is empty',
  'admin.merchants.portfolioEmptyDescription':
    'You do not manage any merchant yet. The merchants you create appear here. Existing or pending shops stay invisible to you until a superadmin assigns them to you.',
  'admin.merchants.portfolioEmptyAction': 'Create a merchant',
  'admin.merchants.noUnassignedTitle': 'No unassigned shop',
  'admin.merchants.searchLabel': 'Search a merchant',
  'admin.merchants.searchPlaceholder': 'Legal name (2 characters minimum)',
  'admin.merchants.scopeLabel': 'Portfolio',
  'admin.merchants.scopeAll': 'All merchants',
  'admin.merchants.scopeUnassigned': 'Unassigned',
  'admin.merchants.table.colManager': 'Managed by',
  'admin.merchants.unassigned': 'Unassigned',
  'admin.merchants.unassignedHint': 'To be assigned to an administrator before any approval.',
  'admin.merchants.pendingForManager': 'To be approved by the managing administrator.',
  'admin.merchants.selectRowAria': 'Select {{name}}',
  'admin.merchants.selectAllAria': 'Select every shop on the page',
  'admin.merchants.assignSelection': 'Assign the selection ({{count}})',
  'admin.merchants.action.assign': 'Assign',
  'admin.merchants.action.reassign': 'Reassign',
  'admin.assign.title': 'Assign to an administrator',
  'admin.assign.intro':
    '{{count}} shop(s) will be given to the chosen administrator, who becomes the only one to see and approve them. The operation is audited.',
  'admin.assign.adminLabel': 'Managing administrator',
  'admin.assign.adminPlaceholder': 'Choose an administrator',
  'admin.assign.noAdmins': 'No active administrator. Create one under « Administrators ».',
  'admin.assign.confirm': 'Assign',
  'admin.assign.success': 'Shop(s) assigned: {{count}}.',
  'admin.portfolios.title': 'Portfolios',
  'admin.portfolios.subtitle':
    'Who manages how many merchants. The superadmin assigns and transfers; it never approves a shop.',
  'admin.portfolios.reasonRequiredDescription':
    'State why you are looking (10 characters minimum) to display the portfolios; every read is audited.',
  'admin.portfolios.loadError': 'Could not load the portfolios.',
  'admin.portfolios.colAdmin': 'Administrator',
  'admin.portfolios.colTotal': 'Merchants',
  'admin.portfolios.colPending': 'Pending',
  'admin.portfolios.colActive': 'Active',
  'admin.portfolios.colSuspended': 'Suspended',
  'admin.portfolios.colRefused': 'Refused',
  'admin.portfolios.disabledBadge': 'Account disabled',
  'admin.portfolios.unassignedRow': 'Unassigned shops',
  'admin.portfolios.unassignedRowHint':
    'Visible to the superadmin only. Nobody can approve them until they are assigned.',
  'admin.portfolios.viewMerchants': 'View merchants',
  'admin.portfolios.assignUnassigned': 'Assign…',
  'admin.portfolios.transfer': 'Transfer the portfolio',
  'admin.portfolios.empty': 'No administrator.',
  'admin.transfer.title': "Transfer {{name}}'s portfolio",
  'admin.transfer.intro':
    "This administrator's {{count}} merchant(s) go to the chosen administrator. It can only be disabled or lose its role once its portfolio is empty.",
  'admin.transfer.targetLabel': 'Receiving administrator',
  'admin.transfer.confirm': 'Transfer',
  'admin.transfer.success': 'Portfolio transferred: {{count}} merchant(s).',
  'admin.organisations.managerLabel': 'Managing administrator',
  'admin.organisations.managerHint':
    'The superadmin is not a manager: choose the administrator who will manage and approve this merchant.',
  'admin.organisations.managerRequired': 'Choose the managing administrator.',
  'admin.organisations.ownPortfolioNote':
    'This merchant will join your portfolio: only you will see and approve it (a superadmin can reassign it).',
  'admin.handover.title': 'Hand-over sheet for the merchant',
  'admin.handover.intro':
    'To give to the merchant. The provisional password is handed over separately: in person, or through a channel you trust.',
  'admin.handover.dashboardLabel': 'Address of the merchant space',
  'admin.handover.dashboardMissing':
    'Address not configured: tell it to the merchant (setting VITE_MERCHANT_DASHBOARD_URL).',
  'admin.handover.accountLabel': 'Identifier (the account e-mail address)',
  'admin.handover.expiresLabel': 'Provisional password valid until',
  'admin.handover.stepsTitle': 'What the merchant has to do',
  'admin.handover.step1':
    'Open the address above and sign in with your e-mail address and the provisional password.',
  'admin.handover.step2': 'Choose your own password (12 characters minimum).',
  'admin.handover.step3':
    'Complete your shop: the public will see it once the platform has approved it.',
  'admin.handover.copy': 'Copy the instructions',
  'admin.handover.copied': 'Instructions copied (without the password).',
  'admin.handover.copyFailed': 'Copying failed: write the instructions down by hand.',
  'admin.handover.noPassword': 'This sheet never contains the password.',
  'admin.organisations.localeLabel': 'Account language',
  'admin.organisations.localeHint': "Language of the merchant's screens at their first sign-in.",
  'admin.merchants.action.reissueUnused': 'New provisional password',
  'admin.merchants.reissueUnusedWarning':
    'Possible while the owner has never signed in. From their first sign-in, only a superadmin can reset their password.',
  'admin.merchants.refuseModalTitle': 'Refuse this shop',
  'admin.merchants.suspendModalTitle': 'Suspend this shop',
  'admin.merchants.refuseWarning':
    'A refusal is final: the shop can no longer be approved and the applicant must register again.',
  'admin.merchants.suspendWarning': "The shop's public storefront disappears immediately.",
  'admin.merchants.resetOwnerTitle': 'New owner password',
  'admin.merchants.resetOwnerWarning':
    "A provisional password is generated and shown only once. The owner's sessions are closed and they must choose a new password at their next sign-in. No email is sent: you hand this password over yourself.",
  'admin.merchants.resetOwnerGenerate': 'Generate the password',
  'admin.organisations.statusActive': 'Shop approved.',
  'admin.organisations.statusPending': 'Shop waiting for approval.',
  'admin.organisations.createAnother': 'Create another account',
  'errors.reauthRequired': 'Confirm your password to continue.',
  'errors.reauthFailed':
    'Wrong password. After several mistakes the account is locked for a few minutes.',
  'errors.emailUnavailable': 'This address cannot be used for a new account.',
  'errors.accountBelongsToShop':
    'This account belongs to a shop: it cannot receive a platform role.',
  'errors.ownerAmbiguous':
    'This shop has several owners: the reset goes through the operator procedure.',
  'errors.ownerDisabled': "The owner's account is disabled.",
  'admin.organisations.submit': 'Create the account',
  'admin.organisations.success': 'Account created.',

  // ---- admin: analytics ----
  'admin.analytics.title': 'Platform analytics',
  'admin.analytics.subtitle': 'Real indicators, computed across every shop on the platform.',
  'admin.analytics.reasonPlaceholder': 'E.g.: weekly platform performance review',
  'admin.analytics.loadError': 'Unable to load analytics',
  'admin.analytics.gmv': 'GMV',
  'admin.analytics.ordersAllShops': 'Orders (all shops)',
  'admin.analytics.activeMerchants': 'Active merchants',
  'admin.analytics.pendingMerchants': 'Pending merchants',
  'admin.analytics.byStatusTitle': 'Merchants by status',
  'admin.analytics.noMerchants': 'No merchant registered',

  // ---- merchant: clients ----
  'merchant.clients.subtitle':
    'Reconstructed from your real orders (no customer list endpoint exists yet) — a customer maps to a phone number.',
  'merchant.clients.truncatedNotice': 'Good to know',
  'merchant.clients.truncatedDescription':
    'This list is built from your 2,000 most recent orders. Beyond that, an older customer may be missing.',
  'merchant.clients.loadError': 'Unable to load customers',
  'merchant.clients.emptyTitle': 'No customers yet',
  'merchant.clients.emptyDescription':
    'Your customers will appear here as soon as your first order arrives.',
  'merchant.clients.table.colClient': 'Customer',
  'merchant.clients.table.colLastActivity': 'Last activity',
  'merchant.clients.table.colTotalSpent': 'Total spent',
  'merchant.clients.viewDetail': 'View detail',
  'merchant.clients.orderCountLabel': 'Order(s)',
  'merchant.clients.orderHistoryTitle': 'Order history',
  'merchant.clients.purchasedProductsTitle': 'Purchased products',
  'merchant.clients.noNonCancelledProducts': 'No product on a non-cancelled order.',
  'merchant.clients.couponsUsedTitle': 'Coupons used',
  'merchant.clients.noCouponUsed': 'No coupon used',
  'merchant.clients.noCouponUsedDescription':
    "This customer has not used any of the shop's coupons yet.",
  'merchant.clients.couponOn': 'on',

  // ---- merchant: marketing ----
  'merchant.marketing.subtitlePrefix':
    'Real performance of your coupons. To create or edit a coupon, go to',
  'merchant.marketing.loadError': 'Unable to load marketing data',
  'merchant.marketing.activeCoupons': 'Active coupons',
  'merchant.marketing.activeCouponsHint': 'Out of {{total}} coupon(s) total',
  'merchant.marketing.totalUsage': 'Total uses',
  'merchant.marketing.couponsCreated': 'Coupons created',
  'merchant.marketing.performanceTitle': 'Promotion performance',
  'merchant.marketing.emptyTitle': 'No coupon yet',
  'merchant.marketing.emptyDescription':
    'Create a coupon from Promotions to see its performance here.',
  'merchant.marketing.table.colCode': 'Code',
  'merchant.marketing.table.colValue': 'Value',
  'merchant.marketing.table.colUsageRate': 'Usage rate',
  'merchant.marketing.usageUnlimited': '{{count}} (unlimited)',
  'merchant.marketing.campaignsTitle': 'Campaigns',
  'merchant.marketing.campaignsDescription':
    'Feature not available yet. No campaign module (email, SMS) currently exists on the platform — only the coupons above are real and usable today.',

  // ---- merchant: payments ----
  'merchant.payments.subtitle':
    'Cash on delivery only. This view is computed from your orders’ status — no separate payment data is exposed on this screen yet.',
  'merchant.payments.loadError': 'Unable to load payments',
  'merchant.payments.truncatedDescription': 'These figures cover your 2,000 most recent orders.',
  'merchant.payments.amountCollected': 'Amount collected',
  'merchant.payments.amountCollectedHint': 'Orders with confirmed payment',
  'merchant.payments.pendingKpi': 'Pending',
  'merchant.payments.confirmedKpi': 'Confirmed',
  'merchant.payments.failedKpi': 'Failed',
  'merchant.payments.filterAria': 'Filter by payment status',
  'merchant.payments.emptyForStatus': 'No order for this status',
  'merchant.payments.table.colPayment': 'Payment',
  'merchant.payments.bucket.pending': 'Pending',
  'merchant.payments.bucket.confirmed': 'Confirmed',
  'merchant.payments.bucket.failed': 'Failed',
  'merchant.payments.bucket.refunded': 'Refunded',
  'merchant.payments.bucket.cancelled': 'Cancelled',

  // ---- merchant: delivery ----
  'merchant.delivery.subtitle': 'Orders awaiting dispatch, with their attempt history.',
  'merchant.delivery.loadError': 'Unable to load shipments',
  'merchant.delivery.emptyTitle': 'No shipment pending',
  'merchant.delivery.emptyDescription':
    'Orders marked "Packed" will appear here if their dispatch has not yet succeeded.',
  'merchant.delivery.tableCaption': 'Pending shipments',
  'merchant.delivery.table.colShipment': 'Shipment',
  'merchant.delivery.table.colAttempts': 'Dispatch attempts',
  'merchant.delivery.table.colDetail': 'Detail',
  'merchant.delivery.table.colLastAttempt': 'Last attempt',
  'merchant.delivery.noDetail': 'No detail for now.',
  'merchant.delivery.status.pendingDispatch': 'Awaiting dispatch',
  'merchant.delivery.status.dispatchFailed': 'Dispatch failed',
  // ---- Expéditions (Jibex) ----
  'merchant.ship.title': 'Shipments',
  'merchant.ship.subtitle':
    'Your parcels at Jibex: their status, followed automatically, and what you can do.',
  'merchant.ship.noAccount': 'No Jibex account is connected: « Packed » orders are not sent.',
  'merchant.ship.noAccountLink': 'Connect Jibex',
  'merchant.ship.tab.in_progress': 'In progress',
  'merchant.ship.tab.to_send': 'To send',
  'merchant.ship.tab.failed': 'Failed sends',
  'merchant.ship.tab.delivered': 'Delivered',
  'merchant.ship.tab.returns': 'Returns and cancellations',
  'merchant.ship.tab.attention': 'Needs attention',
  'merchant.ship.tableCaption': 'Shipments',
  'merchant.ship.col.order': 'Order',
  'merchant.ship.col.recipient': 'Recipient',
  'merchant.ship.col.status': 'Status',
  'merchant.ship.col.tracking': 'Tracking no.',
  'merchant.ship.col.updated': 'Updated',
  'merchant.ship.col.open': 'Detail',
  'merchant.ship.empty.title': 'No shipment here',
  'merchant.ship.empty.description':
    'Orders moved to « Packed » are sent to Jibex and appear here.',
  'merchant.ship.status.pending_dispatch': 'To send',
  'merchant.ship.status.dispatch_failed': 'Send failed',
  'merchant.ship.status.registered': 'Registered at Jibex',
  'merchant.ship.status.pickup_scheduled': 'Pickup scheduled',
  'merchant.ship.status.in_transit': 'In transit',
  'merchant.ship.status.out_for_delivery': 'Out for delivery',
  'merchant.ship.status.delivered': 'Delivered',
  'merchant.ship.status.return_in_progress': 'Returning',
  'merchant.ship.status.returned': 'Returned',
  'merchant.ship.status.under_review': 'Under review',
  'merchant.ship.status.cancelled': 'Cancelled',
  'merchant.ship.status.lost': 'Lost',
  'merchant.ship.status.unknown': 'Unknown status',
  'merchant.ship.attention.under_review': 'Jibex is checking this parcel',
  'merchant.ship.attention.cancelled': 'Cancelled by Jibex: your decision',
  'merchant.ship.attention.lost': 'Parcel lost: contact Jibex',
  'merchant.ship.attention.unknown_status': 'Unknown Jibex status',
  'merchant.ship.attention.unexpected_transition': 'Unexpected change',
  'merchant.ship.attention.credentials': 'Jibex credentials refused',
  'merchant.ship.attention.return': 'Return to follow',
  'merchant.ship.attention.cancellation_refused': 'Cancellation refused by Jibex',
  'merchant.ship.source.carrier_poll': 'Jibex tracking',
  'merchant.ship.source.carrier_notification': 'Jibex notification',
  'merchant.ship.source.create_response': 'parcel created',
  'merchant.ship.source.system': 'ABOUEB',
  'merchant.ship.source.operator': 'your action',
  'merchant.ship.detail.title': 'Shipment',
  'merchant.ship.detail.order': 'Order',
  'merchant.ship.detail.recipient': 'Recipient',
  'merchant.ship.detail.tracking': 'Tracking no.',
  'merchant.ship.detail.copy': 'Copy',
  'merchant.ship.detail.copied': 'Copied',
  'merchant.ship.detail.reference': 'Reference at Jibex',
  'merchant.ship.detail.carrierStatus': 'Jibex status',
  'merchant.ship.detail.reason': 'Reason given by Jibex',
  'merchant.ship.detail.cancellation': 'Cancellation',
  'merchant.ship.detail.cancellationRequested': 'requested, waiting for Jibex’s decision',
  'merchant.ship.detail.cancellationRefused': 'refused by Jibex',
  'merchant.ship.detail.lastError': 'Why the send failed',
  'merchant.ship.detail.nextPoll': 'Next check',
  'merchant.ship.detail.timeline': 'Timeline',
  'merchant.ship.detail.noEvents': 'No event yet.',
  'merchant.ship.detail.amounts': 'Amounts',
  'merchant.ship.detail.amountsNotice':
    'As sent by Jibex. Their exact meaning (fees deducted or not) is not yet confirmed by Jibex: ABOUEB uses them for no calculation, and the order and invoice stay unchanged.',
  'merchant.ship.detail.codSent': 'Amount to collect sent',
  'merchant.ship.detail.journal': 'Calls to Jibex',
  'merchant.ship.action.open': 'Open',
  'merchant.ship.action.refresh': 'Refresh',
  'merchant.ship.action.retry': 'Retry sending',
  'merchant.ship.action.cancel': 'Cancel the parcel',
  'merchant.ship.action.resend': 'Send the order again',
  'merchant.ship.done.refresh': 'Status refreshed.',
  'merchant.ship.auto.note': 'Status refreshed automatically from Jibex every 60 s.',
  'merchant.ship.auto.last': 'Last automatic refresh: {{time}} (every 60 s).',
  'merchant.sendJibex.title': 'Confirm and send to Jibex',
  'merchant.sendJibex.action': 'Send to Jibex',
  'merchant.sendJibex.orderLine': 'Order #{{number}} · {{total}}',
  'merchant.sendJibex.recipient': 'Recipient',
  'merchant.sendJibex.parcel': 'Parcel information',
  'merchant.sendJibex.phone': 'Phone',
  'merchant.sendJibex.phone2': 'Phone 2',
  'merchant.sendJibex.name': 'Name',
  'merchant.sendJibex.address': 'Address',
  'merchant.sendJibex.addressPlaceholder': 'Street, area, number…',
  'merchant.sendJibex.governorate': 'Governorate',
  'merchant.sendJibex.delegation': 'Delegation',
  'merchant.sendJibex.delegationPlaceholder': 'e.g. Bab Bhar',
  'merchant.sendJibex.chooseGovernorateFirst': 'Choose the governorate first',
  'merchant.sendJibex.delegationMismatch': 'Choose a delegation of this governorate.',
  'merchant.sendJibex.typedDelegation':
    'Entered on the order: “{{value}}”. Choose the matching delegation.',
  'merchant.sendJibex.comment': 'Comment',
  'merchant.sendJibex.commentHint': 'Sent to Jibex with the parcel (with Phone 2).',
  'merchant.sendJibex.price': 'Price (TND)',
  'merchant.sendJibex.priceHint': 'The order total, collected on delivery.',
  'merchant.sendJibex.pieces': 'Pieces',
  'merchant.sendJibex.designation': 'Description',
  'merchant.sendJibex.designationHint': 'The order’s items, sent to Jibex.',
  'merchant.sendJibex.weight': 'Weight (kg)',
  'merchant.sendJibex.exchange': 'Exchange',
  'merchant.sendJibex.type': 'Type',
  'merchant.sendJibex.typeFIX': 'FIX - Fixed',
  'merchant.sendJibex.fragile': 'Fragile',
  'merchant.sendJibex.opening': 'Opening allowed',
  'merchant.sendJibex.yes': 'Yes',
  'merchant.sendJibex.no': 'No',
  'merchant.sendJibex.select': 'Select…',
  'merchant.sendJibex.required': 'Required field.',
  'merchant.sendJibex.missingTitle':
    'Some information is missing to create the parcel: fill in the highlighted fields.',
  'merchant.sendJibex.missingField': 'To fill in',
  'merchant.sendJibex.phoneInvalid': '8 digits, with or without +216.',
  'merchant.sendJibex.weightInvalid': 'A weight above 0 kg.',
  'merchant.sendJibex.piecesInvalid': 'A whole number, at least 1.',
  'merchant.sendJibex.submit': 'Create and send',
  'merchant.sendJibex.sending': 'Sending to Jibex…',
  'merchant.sendJibex.success': 'Parcel created at Jibex: {{tracking}}.',
  'merchant.sendJibex.failed':
    'Jibex did not create the parcel: {{reason}} Fix it and send again — what you typed is kept.',
  'merchant.sendJibex.stillSending': 'The send continues in the background: follow it in Delivery.',
  'merchant.sendJibex.alreadySent': 'This order is already at Jibex ({{tracking}}).',
  'merchant.sendJibex.inProgress': 'This order is already being sent.',
  'merchant.sendJibex.notSendable': 'This order cannot be sent in its current status.',
  'merchant.sendJibex.noCarrier':
    'No Jibex account connected: the order will only be marked « Packed », not sent.',
  'merchant.sendJibex.markPacked': 'Mark as packed',
  'merchant.sendJibex.openDelivery': 'See in Delivery',
  'merchant.sendJibex.phone2Invalid': 'Phone 2: 8 digits, with or without +216.',
  'merchant.ship.done.retry': 'Send restarted: the status updates in a moment.',
  'merchant.ship.done.cancel': 'Parcel cancelled at Jibex. The order is « Packed » again.',
  'merchant.ship.done.resend': 'Order sent again: a new parcel is being created.',
  'merchant.ship.cancel.title': 'Cancel this parcel at Jibex?',
  'merchant.ship.cancel.body': 'Only possible while Jibex has not taken the parcel yet.',
  'merchant.ship.cancel.effect':
    'The order goes back to « Packed »: you can correct it and send it again. It is not re-sent automatically.',
  'merchant.ship.cancel.keep': 'Back',
  'merchant.ship.cancel.confirm': 'Cancel the parcel',
  'merchant.ship.city.label': 'City sent to Jibex',
  'merchant.ship.city.hint':
    'Format « Governorate, Delegation » (e.g. Tunis, Bab Bhar). The order’s address is not changed.',
  'merchant.ship.city.save': 'Save the city',
  'merchant.ship.city.reset': 'Back to the order’s address',
  'merchant.ship.city.saved': 'City saved. Retry the send to use it.',
  'merchant.ship.lastError.INVALID_PHONE':
    'The customer’s phone is not an 8-digit Tunisian number.',
  'merchant.ship.lastError.INVALID_CITY': 'The city is not accepted: correct it below, then retry.',
  'merchant.ship.lastError.INVALID_ADDRESS': 'The address is empty or too long.',
  'merchant.ship.lastError.INVALID_NAME': 'The recipient’s name is empty or too long.',
  'merchant.ship.lastError.INVALID_REQUEST':
    'Jibex refused the parcel’s details (often the city): check them, correct the city if needed, then retry.',
  'merchant.ship.lastError.credentials':
    'Jibex refuses the account credentials: check them in « Jibex account ».',
  'merchant.ship.lastError.FORBIDDEN': 'Your Jibex key lacks the required right: ask Jibex for it.',
  'merchant.ship.lastError.ECOMMERCE_STORE_NOT_FOUND':
    'The Jibex store id does not belong to your account.',
  'merchant.ship.lastError.CARRIER_NOT_CONFIGURED': 'The platform is not connected to Jibex yet.',
  'merchant.ship.lastError.other': 'Jibex refused the send (code {{code}}).',
  'merchant.ship.error.notFound': 'Shipment not found.',
  'merchant.ship.error.notRetryable':
    'This send cannot be restarted (it did not fail, or the order is no longer « Packed »).',
  'merchant.ship.error.notAtCarrier': 'This parcel is not at Jibex yet.',
  'merchant.ship.error.notCancellable':
    'This parcel can no longer be cancelled: Jibex has already taken it.',
  'merchant.ship.error.notResendable':
    'This order cannot be sent again (the parcel is not cancelled, or the order is no longer « Packed »).',
  'merchant.ship.error.alreadyAtCarrier':
    'The parcel is already at Jibex: the city can no longer be changed.',
  'merchant.ship.error.invalidCity': 'Invalid city: 1 to 120 characters.',
  'merchant.ship.error.accountUnavailable':
    'No usable Jibex account: connect it in « Jibex account ».',
  'merchant.ship.error.credentialsRefused': 'Jibex refuses your account credentials.',
  'merchant.ship.error.scopeMissing':
    'Your Jibex key lacks the right for this action: ask Jibex for it.',
  'merchant.ship.error.carrierRateLimited': 'Jibex asks to wait a little before retrying.',
  'merchant.ship.error.carrierUnavailable':
    'Jibex is not answering right now. Try again in a few minutes.',
  'merchant.ship.error.carrierRefused': 'Jibex refused this request.',
  'merchant.delivery.notifications.title': 'Jibex notifications',
  'merchant.delivery.notifications.description':
    'Jibex tells ABOUEB about every status change of a parcel: your orders update at once. Without notifications, ABOUEB checks Jibex regularly.',
  'merchant.delivery.notifications.unavailable':
    'This server has no public address to receive notifications. Status follows by regular checks.',
  'merchant.delivery.notifications.needAccount': 'Connect your Jibex account first.',
  'merchant.delivery.notifications.notConfigured': 'Not set up yet.',
  'merchant.delivery.notifications.configured': 'Set up. Address to give Jibex:',
  'merchant.delivery.notifications.generate': 'Create the address and secret',
  'merchant.delivery.notifications.rotate': 'Change the secret',
  'merchant.delivery.notifications.rotateConfirm':
    'The old secret stops working immediately: Jibex must receive the new one.',
  'merchant.delivery.notifications.disable': 'Turn off',
  'merchant.delivery.notifications.revealTitle': 'Address and signing secret',
  'merchant.delivery.notifications.secretOnce': 'The secret is shown only once. Copy it now.',
  'merchant.delivery.notifications.steps':
    'Give this address and this secret to the delivering company (Jibex), which records them in your sender profile.',
  'merchant.delivery.notifications.urlLabel': 'Notification address',
  'merchant.delivery.notifications.secretLabel': 'Signing secret',
  'merchant.delivery.notifications.done': 'I have copied it',
  'merchant.delivery.notifications.errorUnavailable':
    'Notifications cannot be set up on this server.',

  // ---- auth pages (Lot E3) ----
  'auth.email.label': 'Email address',
  'auth.password.label': 'Password',
  'auth.confirmPassword.label': 'Confirm password',
  'auth.password.hint': 'At least 8 characters.',
  'auth.validation.email': 'This email address does not look right.',
  'auth.validation.passwordShort': 'The password must be at least 8 characters.',
  'auth.validation.chosenPasswordShort': 'The password must be at least 12 characters long.',
  'auth.validation.passwordsMismatch': 'The two passwords do not match.',
  // The ABOUEB brand panel of the merchant sign-in screens (features/auth/components/AuthLayout).
  'merchant.authBrand.space': 'Merchant space',
  'merchant.authBrand.headline': 'Open the doors',
  'merchant.authBrand.headlineAccent': 'to your online business.',
  'merchant.authBrand.lead':
    'Manage your products, orders and deliveries, and follow everything in real time, wherever you are.',
  'auth.login.title': 'Sign in',
  'auth.login.subtitle': 'Welcome back to your merchant space.',
  'auth.login.submit': 'Sign in',
  'auth.login.forgot': 'Forgot your password?',
  'auth.login.platformAccountNotice':
    'Did the platform give you a provisional password? Enter it here: you will be asked to choose a new one. It is valid for 72 hours.',
  'auth.login.invalid':
    'Incorrect email or password. If you have tried several times, wait a moment before trying again. Provisional password lost or expired (72 h)? Contact the platform support.',
  'auth.forgot.title': 'Forgot your password',
  'auth.forgot.subtitle': 'Enter your address: we will send you a link to choose a new one.',
  'auth.forgot.submit': 'Send the link',
  'auth.forgot.back': 'Back to sign in',
  'auth.forgot.sentTitle': 'Check your inbox',
  'auth.forgot.sentSubtitle': 'If an account exists with this address, a link has just been sent.',
  'auth.forgot.sentHint':
    'The link expires after a short while. Remember to check your spam folder.',
  'auth.reset.title': 'New password',
  'auth.reset.subtitle': 'Choose a password you do not use anywhere else.',
  'auth.reset.newPassword': 'New password',
  'auth.reset.submit': 'Save password',
  'auth.reset.invalidTitle': 'Incomplete link',
  'auth.reset.invalidSubtitle': 'This link looks like it was cut short.',
  'auth.reset.invalidHint': 'Make sure you copied the whole link, or ask for a new one.',
  'auth.reset.requestNew': 'Ask for a new link',
  'auth.reset.failed':
    'This link is no longer valid: it has expired or was already used. Ask for a new one.',
  'auth.reset.doneTitle': 'Password updated',
  'auth.reset.doneSubtitle': 'You can sign in with your new password.',
  'auth.reset.doneHint': 'For your security, your other sessions have been signed out.',
  'auth.reset.signIn': 'Sign in',
  'auth.changePassword.title': 'Choose your password',
  'auth.changePassword.subtitle':
    'The provisional password you were given is only for this step. Choose one that only you know.',
  'auth.changePassword.currentLabel': 'Provisional password',
  'auth.changePassword.signOut': 'Sign out',
  'auth.wrongSpace.merchant.title': 'Account not linked to a shop',
  'auth.wrongSpace.merchant.subtitle': 'This space is for merchants.',
  'auth.wrongSpace.merchant.body':
    'This account is not linked to any shop. If it is a platform administrator account, sign in to the administration space. Otherwise, contact the platform.',
  'auth.wrongSpace.admin.title': 'Account without a platform role',
  'auth.wrongSpace.admin.subtitle': 'This space is for platform administrators.',
  'auth.wrongSpace.admin.body':
    'This account has no administration role. If it is a merchant account, sign in to the merchant space.',
  'auth.login.noAccountFooter':
    'No account yet? The platform creates merchant accounts: contact your platform contact.',
  'auth.changePassword.welcomeTitle': 'Welcome — choose your password',
  'auth.changePassword.welcomeSubtitle':
    'The platform created your merchant account. The provisional password you were given is only for this step: choose one that only you know.',
  'auth.changePassword.rule': 'At least 12 characters, different from the provisional password.',
  'auth.changePassword.step1': 'Sign in',
  'auth.changePassword.step2': 'Choose my password',
  'auth.changePassword.step3': 'Reach my space',
  'auth.changePassword.steps': 'Steps',
  'auth.changePassword.provisionalWrong':
    'The provisional password is wrong. Type it exactly as it was given to you. After several attempts the account is locked for a while.',
  'auth.forgot.noEmailSubtitle': 'This platform does not send e-mails yet.',
  'auth.forgot.noEmailBody':
    "To get back into your account, contact the platform's support: a new provisional password will be handed to you in person.",
  'merchant.welcome.title': 'Welcome.',
  'merchant.welcome.subtitle': 'Your account was created by the platform.',
  'merchant.welcome.passwordSaved': 'Password saved.',
  'merchant.welcome.statusPending':
    'Your shop is waiting for approval: it will not be visible to the public until the platform has approved it. In the meantime you can prepare it.',
  'merchant.welcome.statusActive':
    'Your shop is approved. Complete it so that it is ready for its first customers.',
  'merchant.welcome.cta': 'Complete my shop',
  'merchant.welcome.dismiss': 'Close',

  // ---- merchant: Jibex account (Lot L5a) ----
  'merchant.delivery.account.title': 'Jibex account',
  'merchant.delivery.account.subtitle':
    "Link your own Jibex account: your shipments go out under your credentials, never another merchant's.",
  'merchant.delivery.account.back': 'Back to shipments',
  'merchant.delivery.account.linkFromDelivery': 'Jibex account',
  'merchant.delivery.account.ownerOnly': 'Only the shop owner can manage the Jibex account.',
  'merchant.delivery.account.loadError': 'Could not load the Jibex account',
  'merchant.delivery.account.platformNotReady':
    'The Jibex connection is not yet enabled on the platform. You can save your credentials; the connection test will be available once it is enabled.',
  'merchant.delivery.account.state.not_configured': 'Not configured',
  'merchant.delivery.account.state.untested': 'Not tested yet',
  'merchant.delivery.account.state.connected': 'Connected',
  'merchant.delivery.account.state.invalid': 'Invalid credentials',
  'merchant.delivery.account.state.misconfigured': 'Needs attention',
  'merchant.delivery.account.state.network_error': 'Network error',
  'merchant.delivery.account.stateText.not_configured':
    'No Jibex account is saved. Enter your client ID and secret below.',
  'merchant.delivery.account.stateText.untested':
    'Credentials saved but not verified yet. Run a connection test.',
  'merchant.delivery.account.stateText.connected': 'Jibex accepted your credentials.',
  'merchant.delivery.account.stateText.invalid':
    'Jibex refused your credentials. Check the client ID and secret, then save them again.',
  'merchant.delivery.account.stateText.misconfigured':
    'Jibex accepted your credentials, but the account cannot be used as it is. Check the store ID or contact Jibex.',
  'merchant.delivery.account.stateText.network_error':
    'The last check could not be completed. Your credentials were not judged invalid: try again in a moment.',
  'merchant.delivery.account.problem.invalidClient': 'Client ID or secret refused by Jibex.',
  'merchant.delivery.account.problem.clientInactive': 'This client account is disabled at Jibex.',
  'merchant.delivery.account.problem.forbidden': 'Jibex refused access for this account.',
  'merchant.delivery.account.problem.storeNotFound':
    'The Jibex store you entered was not found. Check the store ID.',
  'merchant.delivery.account.problem.rateLimited':
    'Jibex is temporarily limiting requests. Try again shortly.',
  'merchant.delivery.account.problem.timeout': 'Jibex did not answer in time.',
  'merchant.delivery.account.problem.unreachable':
    'Jibex could not be reached or answered unexpectedly.',
  'merchant.delivery.account.problem.notActivated':
    'The Jibex connection is not enabled on the platform.',
  'merchant.delivery.account.problem.secretUnreadable':
    'The saved secret cannot be read. Enter it again.',
  'merchant.delivery.account.problem.unknown': 'Unexpected error (code {{code}}).',
  'merchant.delivery.account.form.title': 'Jibex credentials',
  'merchant.delivery.account.form.clientIdLabel': 'Client ID',
  'merchant.delivery.account.form.clientIdHint': 'Provided by Jibex. This is not a secret.',
  'merchant.delivery.account.form.secretLabel': 'Jibex secret',
  'merchant.delivery.account.form.secretHintNew':
    'Paste the secret provided by Jibex. It is encrypted immediately and never shown again.',
  'merchant.delivery.account.form.secretHintKeep':
    'A secret is saved. Leave this field empty to keep it, or enter a new one to replace it.',
  'merchant.delivery.account.form.secretStored': 'Secret saved (encrypted)',
  'merchant.delivery.account.form.storeIdLabel': 'Jibex store ID',
  'merchant.delivery.account.form.storeIdHint':
    'Optional. Only fill it in if Jibex gave you a store ID.',
  'merchant.delivery.account.form.save': 'Save',
  'merchant.delivery.account.form.saved': 'Credentials saved. Run a connection test.',
  'merchant.delivery.account.form.clientIdRequired': 'The client ID is required.',
  'merchant.delivery.account.form.clientIdTooLong':
    'The client ID is too long (200 characters maximum).',
  'merchant.delivery.account.form.secretRequired':
    'The secret is required to save a new client ID.',
  'merchant.delivery.account.form.secretTooLong':
    'The secret is too long (512 characters maximum).',
  'merchant.delivery.account.form.charactersNotAllowed':
    'Only standard printable characters are accepted (no line breaks, no accented characters).',
  'merchant.delivery.account.form.storeIdInvalid': 'The store ID must be a positive whole number.',
  'merchant.delivery.account.form.unsavedHint': 'Save your changes before testing the connection.',
  'merchant.delivery.account.security.title': 'How your secret is protected',
  'merchant.delivery.account.security.body':
    'The secret is encrypted as soon as you enter it. It is never displayed, never sent back by the application and never exported. To change it, enter a new one.',
  'merchant.delivery.account.status.title': 'Account status',
  'merchant.delivery.account.status.lastCheck': 'Last check: {{date}}',
  'merchant.delivery.account.status.neverChecked': 'Never checked',
  'merchant.delivery.account.status.clientId': 'Client ID: {{clientId}}',
  'merchant.delivery.account.test.button': 'Test connection',
  'merchant.delivery.account.test.running': 'Testing…',
  'merchant.delivery.account.test.done': 'Test finished: {{result}}',
  'merchant.delivery.account.test.tooMany': 'Too many tests. Try again in {{seconds}} s.',
  'merchant.delivery.account.test.unavailable':
    'The service is momentarily unavailable. Try again shortly.',
  'merchant.delivery.account.test.saveFirst': 'Save your credentials first.',
  'merchant.delivery.account.history.title': 'Check history',
  'merchant.delivery.account.history.empty': 'No checks yet.',
  'merchant.delivery.account.history.loadError': 'Could not load the history',
  'merchant.delivery.account.history.caption': 'Jibex account check history',
  'merchant.delivery.account.history.colDate': 'Date',
  'merchant.delivery.account.history.colResult': 'Result',
  'merchant.delivery.account.history.colDetail': 'Detail',
  'merchant.delivery.account.history.colClient': 'Client ID',
  'merchant.delivery.account.history.colDuration': 'Duration',
  'merchant.delivery.account.history.durationMs': '{{ms}} ms',
  'merchant.delivery.account.outcome.credentials_saved': 'Credentials saved',
  'merchant.delivery.account.outcome.connected': 'Connected',
  'merchant.delivery.account.outcome.invalid_credentials': 'Credentials refused',
  'merchant.delivery.account.outcome.misconfigured': 'Needs attention',
  'merchant.delivery.account.outcome.network_error': 'Network error',
  'merchant.delivery.account.outcome.rate_limited': 'Rate limited',
  'merchant.delivery.account.outcome.unavailable': 'Service not enabled',
  'merchant.delivery.account.outcome.disconnected': 'Account disconnected',

  // ---- merchant: invoices ----
  'merchant.invoices.subtitle':
    'An invoice is generated automatically for every confirmed order. Fiscal fields (VAT, legal compliance) remain disabled pending their validation.',
  'merchant.invoices.loadError': 'Unable to load invoices',
  'merchant.invoices.emptyTitle': 'No invoice yet',
  'merchant.invoices.emptyDescription':
    'Invoices will appear here as soon as your first order is confirmed.',
  'merchant.invoices.downloadFailed': 'Download failed.',
  'merchant.invoices.table.colInvoice': 'Invoice',
  'merchant.invoices.table.colAmount': 'Amount',
  'merchant.invoices.fiscal': 'Fiscal',
  'merchant.invoices.nonFiscal': 'Non-fiscal',
  'merchant.invoices.downloadPdf': 'Download PDF',
  'merchant.invoices.invoiceNumberPrefix': 'no.',

  // ---- merchant: staff ----
  'merchant.staff.inviteSuccess':
    'Invitation sent if an account exists with that address. Check the list below.',
  'merchant.staff.ownerOnlyTitle': 'Access restricted to the owner',
  'merchant.staff.ownerOnlyDescription': 'Only the shop owner can manage the team.',
  'merchant.staff.subtitle': 'Invite existing ABOUEB members and manage their permissions.',
  'merchant.staff.inviteMember': 'Invite a member',
  'merchant.staff.loadError': 'Unable to load the team',
  'merchant.staff.emptyTitle': 'No member yet',
  'merchant.staff.inviteAction': 'Invite',
  'merchant.staff.createAccount': 'Create an account',
  'merchant.staff.createAccountTitle': "Create a team member's account",
  'merchant.staff.createAccountIntro':
    'A provisional password is generated for this member. You will see it only once; the member must replace it at their first sign-in. No email is sent.',
  'merchant.staff.createAccountSubmit': 'Create the account',
  'merchant.staff.accountCreatedTitle': 'Account created',
  'merchant.staff.reissue': 'New password',
  'merchant.staff.reissueTitle': 'New provisional password',
  'merchant.staff.reissueIntro':
    "This member's current password will stop working and they will be signed out everywhere. State the reason: it is kept in the audit log.",
  'merchant.staff.reissueReason': 'Reason (10 characters minimum)',
  'merchant.staff.reissueSubmit': 'Generate a new password',
  'merchant.staff.managedBadge': 'Account created by the shop',
  'merchant.staff.table.colMember': 'Member',
  'merchant.staff.table.colRole': 'Role',
  'merchant.staff.roleOwner': 'Owner',
  'merchant.staff.roleStaff': 'Staff',
  'merchant.staff.statusSuspended': 'Suspended',
  'merchant.staff.fullAccess': 'Full access',
  'merchant.staff.noPermissions': 'None',
  'merchant.staff.suspendAction': 'Suspend',
  'merchant.staff.reactivateAction': 'Reactivate',
  'merchant.staff.removeAction': 'Remove',
  'merchant.staff.removeConfirm': 'Remove {{email}} from the team?',
  'merchant.staff.suspendedToast': 'Member suspended.',
  'merchant.staff.reactivatedToast': 'Member reactivated.',
  'merchant.staff.removedToast': 'Member removed.',
  'merchant.staff.permissionsUpdatedToast': 'Permissions updated.',
  'merchant.staff.table.colPermissions': 'Permissions',
  'merchant.staff.form.emailLabel': 'Email address',
  'merchant.staff.form.hint':
    'The invitation only works for an address that already has a ABOUEB account. You will not receive a direct confirmation — check the list below after sending.',
  'merchant.staff.permission.catalog': 'Catalogue (products, categories)',
  'merchant.staff.permission.analytics': 'Analytics (read)',

  // ---- merchant: settings ----
  'merchant.settings.subtitle': 'Manage your shop, your team and your preferences in one place.',
  'merchant.settings.storeCardDescription': 'Public profile, hours, policies, SEO and shop health.',
  'merchant.settings.teamCardDescription': "Invitations, roles and access for your team's members.",

  // ---- merchant: analytics ----
  'merchant.analytics.subtitle':
    'Real indicators for your shop — computed directly from your orders, products and stock.',
  'merchant.analytics.loadError': 'Unable to load analytics',
  'merchant.analytics.revenue': 'Revenue',
  'merchant.analytics.revenueHint': 'Across {{count}} non-cancelled order(s)',
  'merchant.analytics.avgOrderValue': 'Average order value',
  'merchant.analytics.avgOrderValueEmptyHint': 'No non-cancelled order to date.',
  'merchant.analytics.cancellationRate': 'Cancellation rate',
  'merchant.analytics.cancellationRateNoOrders': 'No order recorded.',
  'merchant.analytics.cancellationRateNoneCancelled': 'No cancelled order to date.',
  'merchant.analytics.cancellationRateCount': '{{count}} cancelled order(s)',
  'merchant.analytics.stockAvailable': 'Available stock',
  'merchant.activity.title': 'Recent activity',
  'merchant.activity.empty': 'No recent activity',
  'merchant.activity.loadError': 'Could not load recent activity',
  'merchant.activity.reason': 'Reason: {{reason}}',
  'merchant.activity.actor.system': 'Automatic',
  'merchant.activity.actor.customer': 'By the customer',
  'merchant.activity.actor.merchant': 'By the shop',
  'merchant.activity.order.created': 'New order #{{number}}',
  'merchant.activity.order.changed': 'Order #{{number}}: {{to}}',
  'merchant.activity.stock.manual_adjustment': 'Stock adjustment — {{item}}: {{delta}}',
  'merchant.activity.stock.confirmation': 'Stock shipped out — {{item}}: {{delta}}',
  'merchant.activity.stock.release': 'Stock released — {{item}}: {{delta}}',
  'merchant.activity.stock.shipment': 'Shipment — {{item}}: {{delta}}',
  'merchant.activity.stock.return': 'Returned to stock — {{item}}: {{delta}}',
  'merchant.activity.stock.loss': 'Stock loss — {{item}}: {{delta}}',
  'merchant.activity.stock.unknownItem': 'Item',
  'merchant.nav.todo': 'To do',
  'merchant.todo.title': 'To do',
  'merchant.todo.subtitle':
    'What needs your attention now, based on your orders, stock and delivery.',
  'merchant.todo.allClear': 'Nothing to do right now.',
  'merchant.todo.loading': 'Loading…',
  'merchant.todo.sectionError': 'Could not load this section.',
  'merchant.todo.section.orders': 'Orders',
  'merchant.todo.section.stock': 'Stock',
  'merchant.todo.section.delivery': 'Delivery',
  'merchant.todo.section.jibex': 'Jibex account',
  'merchant.todo.severity.urgent': 'Urgent',
  'merchant.todo.severity.watch': 'Keep an eye on',
  'merchant.todo.severity.info': 'Information',
  'merchant.todo.forbidden': 'This section is not available for your role.',
  'merchant.todo.newShop.title': 'Your shop is ready to start',
  'merchant.todo.newShop.description':
    'Nothing to do yet: add your first product and complete your shop to start receiving orders.',
  'merchant.todo.newShop.addProduct': 'Add a product',
  'merchant.todo.newShop.completeShop': 'Complete my shop',
  'merchant.todo.orders.toPrepare': '{{count}} order(s) to prepare',
  'merchant.todo.orders.toPrepareDescription':
    'Confirmed, waiting for you to start preparing them.',
  'merchant.todo.orders.toPack': '{{count}} order(s) to pack',
  'merchant.todo.orders.toPackDescription': 'Being prepared, not yet marked as packed.',
  'merchant.todo.stock.out': '{{count}} item(s) out of stock',
  'merchant.todo.stock.outDescription': 'They can no longer be ordered.',
  'merchant.todo.stock.low': '{{count}} item(s) low on stock',
  'merchant.todo.stock.lowDescription': 'Their stock is below the alert threshold.',
  'merchant.todo.delivery.toShip': '{{count}} packed order(s) waiting to be shipped',
  'merchant.todo.delivery.toShipDescription': 'No parcel has left yet for these orders.',
  'merchant.todo.delivery.failed': '{{count}} shipment(s) failed',
  'merchant.todo.delivery.failedDescription':
    'Sending to the carrier did not go through: open the Delivery page.',
  'merchant.todo.jibex.platformNotReady': 'Jibex delivery unavailable',
  'merchant.todo.jibex.platformNotReadyDescription':
    'The service is not configured on the platform yet.',
  'merchant.todo.jibex.not_configured': 'Jibex account not connected',
  'merchant.todo.jibex.not_configuredDescription': 'Without an account, no parcel can be sent.',
  'merchant.todo.jibex.untested': 'Jibex account to test',
  'merchant.todo.jibex.untestedDescription':
    'The credentials are saved but have not been checked yet.',
  'merchant.todo.jibex.invalid': 'Jibex credentials refused',
  'merchant.todo.jibex.invalidDescription': 'Jibex refused the credentials: update them.',
  'merchant.todo.jibex.misconfigured': 'Jibex account misconfigured',
  'merchant.todo.jibex.misconfiguredDescription':
    'The connection works but the account cannot be used (permissions or store).',
  'merchant.todo.jibex.network_error': 'Jibex unreachable',
  'merchant.todo.jibex.network_errorDescription': 'The last test could not reach Jibex: try again.',
  'merchant.analytics.stockReserved': 'Reserved stock',
  'merchant.analytics.topProductsTitle': 'Most ordered products',
  'merchant.analytics.topProductsEmptyTitle': 'No product ordered yet',
  'merchant.analytics.topProductsEmptyDescription':
    'The ranking will appear here as soon as there is a first non-cancelled order.',
  'merchant.analytics.table.colQuantitySold': 'Quantity sold',

  // ---- merchant: promotions ----
  'merchant.promotions.subtitle':
    'Create and manage the discount coupons that apply to the whole cart.',
  'merchant.promotions.ownerOnlyDescription': 'Only the shop owner can manage coupons.',
  'merchant.promotions.loadError': 'Unable to load coupons',
  'merchant.promotions.emptyTitle': 'No coupon yet',
  'merchant.promotions.emptyDescription':
    'Create your first coupon to offer a discount on the whole cart.',
  'merchant.promotions.editTitle': 'Edit the coupon',
  'merchant.promotions.updated': 'Coupon updated.',
  'merchant.promotions.created': 'Coupon created.',
  'merchant.promotions.deactivated': 'Coupon deactivated.',
  'merchant.promotions.reactivated': 'Coupon reactivated.',
  'merchant.promotions.table.colUsage': 'Usage',
  'merchant.promotions.table.colValidityWindow': 'Validity window',
  'merchant.promotions.statusInactive': 'Deactivated',
  'merchant.promotions.historyAction': 'History',
  'merchant.promotions.deactivateAction': 'Deactivate',
  'merchant.promotions.noDateLimit': 'No date limit',
  'merchant.promotions.form.typeLabel': 'Type',
  'merchant.promotions.form.typeFixed': 'Fixed amount (TND)',
  'merchant.promotions.form.typePercentage': 'Percentage (%)',
  'merchant.promotions.form.valuePercentLabel': 'Value (%)',
  'merchant.promotions.form.valueAmountLabel': 'Value (TND)',
  'merchant.promotions.form.valueHint': 'From 0 to 100.',
  'merchant.promotions.form.minOrderLabel': 'Minimum order amount',
  'merchant.promotions.form.minOrderHint': 'No minimum if left empty.',
  'merchant.promotions.form.usageLimitTotalLabel': 'Total usage limit',
  'merchant.promotions.form.usageLimitHint': 'Unlimited if left empty.',
  'merchant.promotions.form.usageLimitPerCustomerLabel': 'Limit per customer',
  'merchant.promotions.form.validFromLabel': 'Valid from',
  'merchant.promotions.form.validUntilLabel': 'Valid until',
  'merchant.promotions.form.activeCheckbox': 'Active coupon',
  'merchant.promotions.usageDrawer.title': 'History — {{code}}',
  'merchant.promotions.usageDrawer.loadError': 'Unable to load history',
  'merchant.promotions.usageDrawer.neverUsed': 'This coupon has never been used',
  'merchant.promotions.usageDrawer.tableCaption': 'Coupon usages',
  'merchant.promotions.usageDrawer.colDiscount': 'Discount',

  'errors.network': 'Could not reach the server. Check your internet connection and try again.',
  'errors.server': 'Something went wrong on our end. Please try again in a moment.',
  'errors.sessionExpired': 'Your session has expired. Please sign in again.',
  'errors.forbidden': 'You do not have access to this resource.',
  'errors.notFound': 'This resource does not exist or is no longer available.',
  'errors.validation': 'The form has some invalid fields. Please check your information.',
  'errors.conflict': 'This action conflicts with existing data.',
  'errors.rateLimited': 'Too many attempts. Please try again in a moment.',
  'errors.passwordChangeRequired': 'You must first choose a new password.',
  'errors.currentPasswordInvalid': 'The current password is incorrect.',
  'errors.passwordUnchanged': 'Choose a password different from the current one.',
  'errors.passwordTooShort': 'The new password is too short (12 characters at least).',
  'errors.staffAccountUnavailable':
    'This account cannot be created with this address. Try another address.',
  'errors.staffLimitReached': 'This shop has reached the maximum size of its team.',
  'errors.shopNotActive': 'The shop must be activated before team accounts can be created.',
  // Meta Pixel, social links, view shop, manual orders & sales (migration 0096).
  'merchant.store.form.social': 'Social networks',
  'merchant.store.form.socialHint':
    'Paste the address of your pages. Only the networks you fill in are shown at the top of your shop.',
  'merchant.store.form.socialWhatsappNote':
    'WhatsApp is set in the « Contact » section: it is shown with your networks too.',
  'merchant.store.form.socialFacebook': 'Facebook page',
  'merchant.store.form.socialInstagram': 'Instagram account',
  'merchant.store.form.socialTiktok': 'TikTok account',
  'merchant.store.form.socialYoutube': 'YouTube channel',
  'merchant.store.form.socialX': 'X (Twitter) account',
  'merchant.store.form.socialLinkedin': 'LinkedIn page',
  'merchant.store.form.metaPixel': 'Meta Pixel (Facebook & Instagram)',
  'merchant.store.form.metaPixelLabel': 'Pixel ID',
  'merchant.store.form.metaPixelHint':
    'Your pixel’s number, in Meta Events Manager (Data sources). Leave empty to track nothing.',
  'merchant.store.form.metaPixelPrivacy':
    'The pixel only loads after the visitor agrees (cookie banner). It tracks views, add to cart, checkout start and placed orders.',
  'merchant.viewShop.label': 'View shop',
  'merchant.viewShop.short': 'Shop',
  'merchant.viewShop.notPublic': 'Your shop is not visible online yet.',
  'merchant.viewShop.noStore': 'First create your shop: name and public address.',
  'merchant.nav.sales': 'Sales',
  'merchant.orders.add': 'Add an order',
  'merchant.orders.sourceManual': 'Entered manually',
  'merchant.orders.sourceSale': 'Manual sale',
  'merchant.sales.title': 'Sales',
  'merchant.sales.subtitle':
    'Sales you recorded by hand (physical shop, market, phone…). They count in revenue like online orders, exactly once.',
  'merchant.sales.add': 'Add a sale',
  'merchant.sales.count': 'Recorded sales',
  'merchant.sales.paidAmount': 'Collected (this page)',
  'merchant.sales.toCollect': 'To collect (this page)',
  'merchant.sales.loadError': 'Could not load the sales.',
  'merchant.sales.empty': 'No sale recorded yet',
  'merchant.sales.emptyHint':
    'Record here a sale made outside the online shop: stock and revenue are updated.',
  'merchant.sales.colPayment': 'Payment',
  'merchant.sales.colDate': 'Sale date',
  'merchant.sales.previous': 'Previous',
  'merchant.sales.next': 'Next',
  'merchant.manual.newOrderTitle': 'Add an order',
  'merchant.manual.newOrderSubtitle':
    'An order received by phone, message or in person. It follows the usual flow: preparation, parcel, delivery.',
  'merchant.manual.newSaleTitle': 'Add a sale',
  'merchant.manual.newSaleSubtitle':
    'A sale already handed over to the customer. Stock is updated and an invoice is created.',
  'merchant.manual.backToOrders': '← Orders',
  'merchant.manual.backToSales': '← Sales',
  'merchant.manual.customer': 'Customer',
  'merchant.manual.existingCustomer': 'Existing customer',
  'merchant.manual.newCustomer': '— New customer —',
  'merchant.manual.customerName': 'Customer name',
  'merchant.manual.customerPhone': 'Phone',
  'merchant.manual.customerPhoneHint': 'Tunisian number, with or without +216.',
  'merchant.manual.customerEmail': 'Email',
  'merchant.manual.products': 'Products',
  'merchant.manual.loadingProducts': 'Loading the catalogue…',
  'merchant.manual.noActiveProducts':
    'No active product: activate at least one product to record an order.',
  'merchant.manual.product': 'Product',
  'merchant.manual.chooseProduct': 'Choose a product',
  'merchant.manual.variant': 'Variant',
  'merchant.manual.chooseVariant': 'Choose',
  'merchant.manual.quantity': 'Quantity',
  'merchant.manual.removeLine': 'Remove',
  'merchant.manual.addLine': '+ Add a product',
  'merchant.manual.stockAvailable': '{{count}} in stock',
  'merchant.manual.stockUntracked': 'Stock not tracked',
  'merchant.manual.stockShort': 'Not enough stock',
  'merchant.manual.coupon': 'Coupon code',
  'merchant.manual.couponHint':
    'Applied by its rules (dates, minimum, limits), checked when saving.',
  'merchant.manual.delivery': 'Delivery',
  'merchant.manual.handover': 'Handover',
  'merchant.manual.pickup': 'Handed over in the shop (no delivery)',
  'merchant.manual.governorate': 'Governorate',
  'merchant.manual.shopGovernorate': 'Shop’s governorate',
  'merchant.manual.chooseGovernorate': 'Choose…',
  'merchant.manual.chooseGovernorateFirst': 'Choose the governorate first',
  'merchant.manual.errors.delegationMismatch': 'This delegation is not in the chosen governorate.',
  'merchant.manual.delegation': 'Delegation',
  'merchant.manual.address': 'Address',
  'merchant.manual.additionalInfo': 'Address details',
  'merchant.manual.applyDeliveryFee': 'Apply the shop’s delivery fee ({{fee}})',
  'merchant.manual.paymentAndStatus': 'Payment and status',
  'merchant.manual.status': 'Order status',
  'merchant.manual.status.toProcess': 'To process',
  'merchant.manual.status.toProcessHint': 'It appears in « To do », like an online order.',
  'merchant.manual.status.processing': 'In preparation',
  'merchant.manual.status.processingHint': 'You already started it: next step, the parcel.',
  'merchant.manual.paymentMethod': 'Payment method',
  'merchant.manual.paymentStatus': 'Payment status',
  'merchant.manual.paymentPaid': 'Paid',
  'merchant.manual.paymentPending': 'To collect',
  'merchant.manual.orderPaymentPending':
    'Payment to collect: a manual order is never recorded as paid.',
  'merchant.manual.paymentReference': 'Payment reference',
  'merchant.manual.paymentReferenceHint':
    'Transfer, cheque or card receipt number. Required for a sale paid other than in cash.',
  'merchant.manual.saleDate': 'Sale date',
  'merchant.manual.saleDateHint': 'At most 30 days back, never in the future.',
  'merchant.manual.internalNote': 'Internal note',
  'merchant.manual.internalNoteHint': 'Visible to your team only, never to the customer.',
  'merchant.manual.subtotal': 'Subtotal',
  'merchant.manual.deliveryFee': 'Delivery',
  'merchant.manual.discount': 'Discount',
  'merchant.manual.discountOnSave': 'computed when saving',
  'merchant.manual.estimatedTotal': 'Estimated total',
  'merchant.manual.serverRecomputes':
    'Prices, discount, fees and total are recomputed by the server from the catalogue; stock is checked at the same time.',
  'merchant.manual.saveOrder': 'Save the order',
  'merchant.manual.saveSale': 'Save the sale',
  'merchant.manual.saving': 'Saving…',
  'merchant.manual.orderCreated': 'Order #{{number}} created — {{total}}.',
  'merchant.manual.saleCreated': 'Sale #{{number}} recorded — {{total}}.',
  'merchant.manual.payment.cod': 'Cash on delivery',
  'merchant.manual.payment.cash': 'Cash',
  'merchant.manual.payment.bankTransfer': 'Bank transfer',
  'merchant.manual.payment.card': 'Bank card',
  'merchant.manual.payment.cheque': 'Cheque',
  'merchant.manual.payment.other': 'Other',
  'merchant.manual.errors.fixForm': 'Check the highlighted fields.',
  'merchant.manual.errors.nameRequired': 'The customer name is required.',
  'merchant.manual.errors.phoneInvalid': 'Invalid Tunisian phone number.',
  'merchant.manual.errors.emailInvalid': 'Invalid email address.',
  'merchant.manual.errors.productRequired': 'Choose a product.',
  'merchant.manual.errors.variantRequired': 'Choose a variant.',
  'merchant.manual.errors.quantityInvalid': 'Quantity between 1 and 999.',
  'merchant.manual.errors.duplicateLine': 'This product is already listed: change its quantity.',
  'merchant.manual.errors.governorateRequired': 'Choose a governorate.',
  'merchant.manual.errors.delegationRequired': 'The delegation is required.',
  'merchant.manual.errors.addressRequired': 'The address is required.',
  'merchant.manual.errors.referenceRequired': 'Enter the payment reference.',
  'merchant.manual.errors.saleDate': 'Sale date: at most 30 days back, not in the future.',
  'errors.saleDateOutOfRange':
    'The sale date must be within the last 30 days, and not in the future.',
  'errors.storeNotConfigured': 'Configure your shop first (Shop menu).',
  'errors.requestInProgress': 'This entry is already being saved. Wait a few seconds.',
  'storefront.social.label': 'Follow us',
  'storefront.social.open': '{{network}} (new tab)',
  'storefront.consent.title': 'Advertising cookies',
  'storefront.consent.text':
    'This shop uses the Meta pixel (Facebook, Instagram) to measure its ads, only if you accept. Cookies needed for the cart stay on.',
  'storefront.consent.accept': 'Accept',
  'storefront.consent.decline': 'Decline',
  'storefront.consent.manage': 'Cookies',
  // Order attribution — « Ventes par réseau » (migration 0097).
  'merchant.attribution.title': 'Sales by channel',
  'merchant.attribution.total': 'Total',
  'merchant.attribution.subtitle':
    'Where your orders come from: social apps, ads, search or orders entered by hand.',
  'merchant.attribution.empty': 'No order to analyse yet.',
  'merchant.attribution.orders': '{{count}} order{{plural}}',
  'merchant.attribution.average': 'Average basket: {{amount}}',
  'merchant.attribution.howItWorks':
    'The channel credited is the one of the customer’s last visit that came from a network or an ad (a direct visit never erases it): a link with utm parameters (the « Share my shop » links have them), a Meta / TikTok / Google ad, or the app’s in-app browser (Instagram, Facebook, TikTok…). « Not recorded »: older orders or unknowable origin. No personal data is collected.',
  'merchant.attribution.via': 'via {{source}}',
  'merchant.attribution.email': 'Email',
  'merchant.attribution.referral': 'Other website',
  'merchant.attribution.other': 'Other',
  'merchant.attribution.direct': 'Direct',
  'merchant.attribution.phone': 'Phone',
  'merchant.attribution.inStore': 'In store',
  'merchant.attribution.manual': 'Entered manually (no channel)',
  'merchant.attribution.unknown': 'Not recorded',
  'merchant.attribution.channel': 'Received via',
  'merchant.attribution.channelHint':
    'The channel the customer contacted you through: counted in « Sales by channel ».',
  'merchant.attribution.channelNone': 'Not specified',
  'merchant.attribution.bioLinks': 'Links for your social profiles',
  'merchant.attribution.bioLinksHint':
    'Each link is tagged for its network: orders placed after a click show « via Instagram », « via TikTok »…',
  'merchant.attribution.copyFor': 'Copy for {{source}}',
  // Real-time dashboard (migration 0098).
  'merchant.live.on': 'Live',
  'merchant.live.connecting': 'Connecting…',
  'merchant.live.off': 'Offline',
  'merchant.newOrder.title': 'New order #{{number}}',
  'merchant.newOrder.view': 'View orders',
  'merchant.newOrder.dismiss': 'Dismiss',
  'merchant.newOrder.enableDesktop': 'Get new orders as notifications on this device',
  'merchant.cancelOrder.action': 'Cancel',
  'merchant.cancelOrder.title': 'Cancel order #{{number}}',
  'merchant.cancelOrder.intro':
    'The order becomes “Cancelled” and its reserved items go back on sale. This cannot be undone.',
  'merchant.cancelOrder.reasonLegend': 'Reason for cancelling',
  'merchant.cancelOrder.reason.unreachable': 'Customer unreachable',
  'merchant.cancelOrder.reason.outOfStock': 'Out of stock',
  'merchant.cancelOrder.reason.duplicate': 'Duplicate order',
  'merchant.cancelOrder.reason.customerChanged': 'Customer changed their mind',
  'merchant.cancelOrder.reason.other': 'Other',
  'merchant.cancelOrder.detail': 'Details',
  'merchant.cancelOrder.detailRequired': 'Give the reason.',
  'merchant.cancelOrder.reasonRequired': 'Choose why the order is cancelled.',
  'merchant.cancelOrder.parcelAtCarrier':
    'The parcel is already at Jibex: cancel the parcel in Delivery first, then the order.',
  'merchant.cancelOrder.keep': 'Keep the order',
  'merchant.cancelOrder.confirm': 'Cancel the order',
  'merchant.cancelOrder.done': 'Order #{{number}} cancelled.',
  'merchant.push.title': 'New orders on this phone',
  'merchant.push.offHint':
    'Get every new order in the notification bar, even when the site is closed.',
  'merchant.push.onHint': 'On for this device: every new order arrives in the notification bar.',
  'merchant.push.turnOn': 'Turn on notifications',
  'merchant.push.turnOff': 'Turn off on this device',
  'merchant.push.enabled': 'Done. You will be told about every new order.',
  'merchant.push.disabled': 'Turned off on this device.',
  'merchant.push.blocked':
    'Notifications are blocked: allow them for this site in the browser settings.',
  'merchant.push.failed': 'It did not work. Try again in a moment.',
  'merchant.push.iosIntro': 'On iPhone, Apple first asks you to add ABOUEB to the home screen:',
  'merchant.push.iosStep1': 'In Safari, tap the Share button.',
  'merchant.push.iosStep2': 'Choose “Add to Home Screen”, then “Add”.',
  'merchant.push.iosStep3': 'Open ABOUEB from the new icon and turn notifications on here.',
  'merchant.live.hint':
    'Orders and parcels update by themselves, no « Refresh » needed. Offline: refreshed every 30 seconds.',
};
